/**
 * @license Angular l10n
 * Copyright Roberto Simonetti
 * MIT license
 * https://github.com/robisim74/angular-l10n
 */

import { Injectable, Injector, InjectionToken, Inject, Optional, ChangeDetectorRef, Pipe, Input, Directive, ElementRef, Renderer2, forwardRef, Component, HostBinding, NgModule } from '@angular/core';
import { ReplaySubject, Subject, Observable, BehaviorSubject, race, combineLatest, merge, concat } from 'rxjs';
import { __awaiter } from 'tslib';
import { Router, NavigationStart, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { filter, timeout, map, takeUntil } from 'rxjs/operators';
import { HttpClient, HttpHeaders, HttpParams, HTTP_INTERCEPTORS } from '@angular/common/http';
import { Title, Meta, DomSanitizer } from '@angular/platform-browser';
import { NG_VALIDATORS } from '@angular/forms';

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Allows to get the dependencies at the root level.
 */
class InjectorRef {
    /**
     * @param {?} injector
     */
    constructor(injector) {
        this.injector = injector;
        /**
         * TranslationService instances.
         */
        this.translations = [];
        InjectorRef.injector = this.injector;
    }
    /**
     * @template T
     * @param {?} token
     * @param {?=} notFoundValue
     * @return {?}
     */
    static get(token, notFoundValue) {
        return InjectorRef.injector.get(token, notFoundValue);
    }
}
InjectorRef.injector = Injector.NULL;
InjectorRef.decorators = [
    { type: Injectable }
];
/** @nocollapse */
InjectorRef.ctorParameters = () => [
    { type: Injector }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** @type {?} */
const L10N_CONFIG = new InjectionToken('L10N_CONFIG');
/**
 * @param {?} l10nConfig
 * @return {?}
 */
function l10nConfigFactory(l10nConfig) {
    return {
        locale: l10nConfig.locale || {},
        translation: l10nConfig.translation || {},
        logger: l10nConfig.logger || {},
        localizedRouting: l10nConfig.localizedRouting || {},
        search: l10nConfig.search || {},
        localeInterceptor: l10nConfig.localeInterceptor || {}
    };
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** @enum {number} */
const StorageStrategy = {
    Session: 0,
    Local: 1,
    Cookie: 2,
    Disabled: 3,
};
StorageStrategy[StorageStrategy.Session] = 'Session';
StorageStrategy[StorageStrategy.Local] = 'Local';
StorageStrategy[StorageStrategy.Cookie] = 'Cookie';
StorageStrategy[StorageStrategy.Disabled] = 'Disabled';
/** @enum {number} */
const ProviderType = {
    Fallback: 0,
    Static: 1,
    WebAPI: 2,
};
ProviderType[ProviderType.Fallback] = 'Fallback';
ProviderType[ProviderType.Static] = 'Static';
ProviderType[ProviderType.WebAPI] = 'WebAPI';
/** @enum {string} */
const ISOCode = {
    Language: 'languageCode',
    Country: 'countryCode',
    Script: 'scriptCode',
};
/** @enum {string} */
const ExtraCode = {
    NumberingSystem: 'numberingSystem',
    Calendar: 'calendar',
    Currency: 'currency',
    Timezone: 'timezone',
};
/** @enum {string} */
const LogLevel = {
    Error: 'error',
    Warn: 'warn',
    Info: 'info',
    Log: 'log',
    Off: 'off',
};
/** @enum {number} */
const NumberFormatStyle = {
    Decimal: 0,
    Percent: 1,
    Currency: 2,
};
NumberFormatStyle[NumberFormatStyle.Decimal] = 'Decimal';
NumberFormatStyle[NumberFormatStyle.Percent] = 'Percent';
NumberFormatStyle[NumberFormatStyle.Currency] = 'Currency';
/** @type {?} */
const NUMBER_FORMAT_REGEXP = /^(\d+)?\.((\d+)(\-(\d+))?)?$/;
/** @type {?} */
const ISO8601_DATE_REGEX = /^(\d{4})-?(\d\d)-?(\d\d)(?:T(\d\d)(?::?(\d\d)(?::?(\d\d)(?:\.(\d+))?)?)?(Z|([+-])(\d\d):?(\d\d))?)?$/;
/** @type {?} */
const FORMAT_ALIASES = {
    'short': { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' },
    'medium': { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' },
    'long': { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' },
    'full': { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' },
    'shortDate': { year: 'numeric', month: 'numeric', day: 'numeric' },
    'mediumDate': { year: 'numeric', month: 'short', day: 'numeric' },
    'longDate': { year: 'numeric', month: 'long', day: 'numeric' },
    'fullDate': { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' },
    'shortTime': { hour: 'numeric', minute: 'numeric' },
    'mediumTime': { hour: 'numeric', minute: 'numeric', second: 'numeric' }
};
/** @type {?} */
const LOG_MESSAGES = {
    'missingOnInit': "Missing 'ngOnInit' method: required by AoT compilation",
    'missingOnDestroy': "Missing 'ngOnDestroy' method to cancel subscriptions: required by AoT compilation",
    'missingLang': "Missing 'lang' parameter",
    'missingDefaultLocale': "Missing 'defaultLocale' parameter",
    'missingCurrency': "Missing 'currency' parameter",
    'invalidNumberFormatAlias': "Invalid number format alias: the default format will be used",
    'invalidDateFormatAlias': "Invalid date format alias: the default format will be used"
};

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
class Logger {
    /**
     * @param {?} configuration
     */
    constructor(configuration) {
        this.configuration = configuration;
        Logger.level = this.configuration.logger.level || LogLevel.Off;
        if (Logger.level != LogLevel.Off) {
            Logger.buffer.subscribe((log) => {
                this.send(log);
            });
        }
    }
    /**
     * @param {?} name
     * @param {?} message
     * @return {?}
     */
    static log(name, message) {
        if (Logger.level == LogLevel.Off)
            return;
        Logger.buffer.next({ name: name, message: message });
    }
    /**
     * @param {?} log
     * @return {?}
     */
    send(log) {
        /** @type {?} */
        const message = `angular-l10n (${log.name}): ${LOG_MESSAGES[log.message]}`;
        ((/** @type {?} */ (console)))[(/** @type {?} */ (Logger.level))](message);
    }
}
Logger.level = null;
Logger.buffer = new ReplaySubject();
Logger.decorators = [
    { type: Injectable }
];
/** @nocollapse */
Logger.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Inject, args: [L10N_CONFIG,] }] }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
class Caching {
    constructor() {
        this.cache = {};
    }
    /**
     * @param {?} key
     * @param {?} request
     * @return {?}
     */
    read(key, request) {
        /** @type {?} */
        const cached = this.cache[key];
        if (cached)
            return cached;
        /** @type {?} */
        const buffer = new ReplaySubject(1);
        request.subscribe((value) => buffer.next(value), (error) => {
            buffer.error(error);
            this.cache[key] = undefined;
        }, () => buffer.complete());
        /** @type {?} */
        const response = buffer.asObservable();
        this.cache[key] = response;
        return response;
    }
}
Caching.decorators = [
    { type: Injectable }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Provides the methods to check if Intl APIs are supported.
 */
class IntlAPI {
    /**
     * @return {?}
     */
    static hasIntl() {
        /** @type {?} */
        const hasIntl = typeof Intl === "object" && !!Intl;
        return hasIntl;
    }
    /**
     * @return {?}
     */
    static hasDateTimeFormat() {
        return IntlAPI.hasIntl() && Intl.hasOwnProperty("DateTimeFormat");
    }
    /**
     * @return {?}
     */
    static hasTimezone() {
        if (IntlAPI.hasIntl() && IntlAPI.hasDateTimeFormat()) {
            try {
                new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles' }).format(new Date());
            }
            catch (e) {
                return false;
            }
            return true;
        }
        return false;
    }
    /**
     * @return {?}
     */
    static hasNumberFormat() {
        return IntlAPI.hasIntl() && Intl.hasOwnProperty("NumberFormat");
    }
    /**
     * @return {?}
     */
    static hasCollator() {
        return IntlAPI.hasIntl() && Intl.hasOwnProperty("Collator");
    }
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @param {?} name
 * @return {?}
 */
function getLocalStorage(name) {
    return localStorage.getItem(name);
}
/**
 * @param {?} name
 * @return {?}
 */
function getSessionStorage(name) {
    return sessionStorage.getItem(name);
}
/**
 * @param {?} name
 * @return {?}
 */
function getCookie(name) {
    /** @type {?} */
    let result = null;
    if (typeof document !== "undefined") {
        result = new RegExp("(?:^|; )" + encodeURIComponent(name) + "=([^;]*)").exec(document.cookie);
    }
    return result ? result[1] : null;
}
/**
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
function setLocalStorage(name, value) {
    localStorage.setItem(name, value);
}
/**
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
function setSessionStorage(name, value) {
    sessionStorage.setItem(name, value);
}
/**
 * @param {?} name
 * @param {?} value
 * @param {?=} expiration
 * @return {?}
 */
function setCookie(name, value, expiration) {
    /** @type {?} */
    let expires = "";
    if (expiration != null) {
        /** @type {?} */
        const expirationDate = new Date();
        expirationDate.setTime(expirationDate.getTime() +
            (expiration * 24 * 60 * 60 * 1000));
        expires = "; expires=" + expirationDate.toUTCString();
    }
    if (typeof document !== "undefined") {
        document.cookie = name + "=" + value + expires + "; path=/";
    }
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Implement this class-interface to create a custom storage for default locale, currency & timezone.
 * @abstract
 */
class LocaleStorage {
}
LocaleStorage.decorators = [
    { type: Injectable }
];
class L10nStorage {
    /**
     * @param {?} configuration
     */
    constructor(configuration) {
        this.configuration = configuration;
        this.hasCookie = typeof navigator !== "undefined" && navigator.cookieEnabled;
        this.hasStorage = typeof Storage !== "undefined";
    }
    /**
     * @param {?} name
     * @return {?}
     */
    read(name) {
        return __awaiter(this, void 0, void 0, function* () {
            /** @type {?} */
            let value = null;
            if (this.configuration.locale.storage != StorageStrategy.Disabled) {
                if (this.configuration.locale.storage == StorageStrategy.Local && this.hasStorage) {
                    value = getLocalStorage(this.getName(name));
                }
                else if (this.configuration.locale.storage == StorageStrategy.Session && this.hasStorage) {
                    value = getSessionStorage(this.getName(name));
                }
                else if (this.configuration.locale.storage == StorageStrategy.Cookie && this.hasCookie) {
                    value = getCookie(this.getName(name));
                }
            }
            return value;
        });
    }
    /**
     * @param {?} name
     * @param {?} value
     * @return {?}
     */
    write(name, value) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.configuration.locale.storage != StorageStrategy.Disabled) {
                if (this.configuration.locale.storage == StorageStrategy.Local && this.hasStorage) {
                    setLocalStorage(this.getName(name), value);
                }
                else if (this.configuration.locale.storage == StorageStrategy.Session && this.hasStorage) {
                    setSessionStorage(this.getName(name), value);
                }
                else if (this.configuration.locale.storage == StorageStrategy.Cookie && this.hasCookie) {
                    setCookie(this.getName(name), value, this.configuration.locale.cookieExpiration);
                }
            }
        });
    }
    /**
     * @param {?} name
     * @return {?}
     */
    getName(name) {
        if (this.configuration.locale.storageNames) {
            return ((/** @type {?} */ (this.configuration.locale.storageNames)))[name] || name;
        }
        return name;
    }
}
L10nStorage.decorators = [
    { type: Injectable }
];
/** @nocollapse */
L10nStorage.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Inject, args: [L10N_CONFIG,] }] }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
class DefaultLocale$1 {
    /**
     * @return {?}
     */
    get value() {
        return this.formattedValue;
    }
    /**
     * @param {?} defaultLocale
     * @return {?}
     */
    set value(defaultLocale) {
        this.formattedValue = defaultLocale;
        this.parseValue();
    }
    /**
     * @param {?} languageCode
     * @param {?=} countryCode
     * @param {?=} scriptCode
     * @param {?=} numberingSystem
     * @param {?=} calendar
     * @return {?}
     */
    build(languageCode, countryCode, scriptCode, numberingSystem, calendar) {
        this.languageCode = languageCode;
        this.scriptCode = scriptCode;
        this.countryCode = countryCode;
        this.numberingSystem = numberingSystem;
        this.calendar = calendar;
        /** @type {?} */
        let value = languageCode;
        value += !!scriptCode ? "-" + scriptCode : "";
        value += !!countryCode ? "-" + countryCode : "";
        // Adds the 'u' (Unicode) extension.
        value += (!!numberingSystem || !!calendar) ? "-u" : "";
        value += !!numberingSystem ? "-nu-" + numberingSystem : "";
        value += !!calendar ? "-ca-" + calendar : "";
        this.formattedValue = value;
    }
    /**
     * @return {?}
     */
    parseValue() {
        if (!!this.value) {
            /** @type {?} */
            let value = this.value;
            // Looks for the 'u' (Unicode) extension.
            /** @type {?} */
            const index = value.search("-u");
            if (index != -1) {
                /** @type {?} */
                const extensions = value.substring(index + 1).split("-");
                switch (extensions.length) {
                    case 3:
                        if (extensions[1] == "nu") {
                            this.numberingSystem = extensions[2];
                            this.calendar = undefined;
                        }
                        else if (extensions[1] == "ca") {
                            this.numberingSystem = undefined;
                            this.calendar = extensions[2];
                        }
                        break;
                    default:
                        this.numberingSystem = extensions[2];
                        this.calendar = extensions[4];
                }
                // Extracts the codes.
                value = value.substring(0, index);
            }
            /** @type {?} */
            const codes = value.split("-");
            switch (codes.length) {
                case 1:
                    this.languageCode = codes[0];
                    this.scriptCode = undefined;
                    this.countryCode = undefined;
                    break;
                case 2:
                    this.languageCode = codes[0];
                    this.scriptCode = undefined;
                    this.countryCode = codes[1];
                    break;
                default:
                    this.languageCode = codes[0];
                    this.scriptCode = codes[1];
                    this.countryCode = codes[2];
            }
        }
    }
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
class IntlFormatter {
    /**
     * @param {?} value
     * @param {?} defaultLocale
     * @param {?} style
     * @param {?=} digits
     * @param {?=} currency
     * @param {?=} currencyDisplay
     * @return {?}
     */
    static formatNumber(value, defaultLocale, style, digits, currency, currencyDisplay) {
        if (!IntlAPI.hasNumberFormat())
            return currency ? value + " " + currency : value;
        value = typeof value === "string" && !isNaN(+value - parseFloat(value)) ? +value : value;
        return IntlFormatter.numberFormatter(value, defaultLocale, style, digits, currency, currencyDisplay);
    }
    /**
     * @param {?} value
     * @param {?} defaultLocale
     * @param {?} format
     * @param {?=} timezone
     * @return {?}
     */
    static formatDate(value, defaultLocale, format, timezone) {
        if (!IntlAPI.hasDateTimeFormat())
            return value;
        /** @type {?} */
        let date;
        if (typeof value === "string") {
            value = value.trim();
        }
        if (IntlFormatter.isDate(value)) {
            date = value;
        }
        else if (!isNaN(value - parseFloat(value))) {
            date = new Date(parseFloat(value));
        }
        else if (typeof value === "string" && /^(\d{4}-\d{1,2}-\d{1,2})$/.test(value)) {
            const [y, m, d] = value.split('-').map((val) => parseInt(val, 10));
            date = new Date(y, m - 1, d);
        }
        else {
            date = new Date(value);
        }
        if (!IntlFormatter.isDate(date)) {
            /** @type {?} */
            let match;
            if ((typeof value === "string") && (match = value.match(ISO8601_DATE_REGEX))) {
                date = IntlFormatter.isoStringToDate(match);
            }
        }
        return IntlFormatter.dateTimeFormatter(date, defaultLocale, format, timezone);
    }
    /**
     * @param {?} num
     * @param {?} defaultLocale
     * @param {?} style
     * @param {?=} digits
     * @param {?=} currency
     * @param {?=} currencyDisplay
     * @return {?}
     */
    static numberFormatter(num, defaultLocale, style, digits, currency, currencyDisplay) {
        /** @type {?} */
        let options = {};
        if (digits) {
            if (typeof digits === "string") {
                /** @type {?} */
                const digitsOptions = formatDigitsAliases(digits);
                if (digitsOptions != null) {
                    options = digitsOptions;
                }
                else {
                    Logger.log('IntlFormatter', 'invalidNumberFormatAlias');
                }
            }
            else {
                options = digits;
            }
        }
        options.style = NumberFormatStyle[style].toLowerCase();
        if (style == NumberFormatStyle.Currency) {
            options.currency = currency;
            options.currencyDisplay = currencyDisplay;
        }
        return new Intl.NumberFormat(defaultLocale, options).format(num);
    }
    /**
     * @param {?} date
     * @param {?} defaultLocale
     * @param {?} format
     * @param {?=} timezone
     * @return {?}
     */
    static dateTimeFormatter(date, defaultLocale, format, timezone) {
        /** @type {?} */
        let options = {};
        if (format) {
            if (typeof format === "string") {
                /** @type {?} */
                const dateTimeOptions = FORMAT_ALIASES[format];
                if (dateTimeOptions) {
                    options = dateTimeOptions;
                }
                else {
                    Logger.log('IntlFormatter', 'invalidDateFormatAlias');
                }
            }
            else {
                options = format;
            }
        }
        options.timeZone = IntlAPI.hasTimezone() ? timezone : 'UTC';
        return new Intl.DateTimeFormat(defaultLocale, options).format(date).replace(/[\u200e\u200f]/g, "");
    }
    /**
     * @param {?} value
     * @return {?}
     */
    static isDate(value) {
        return value instanceof Date && !isNaN(value.valueOf());
    }
    /**
     * @param {?} match
     * @return {?}
     */
    static isoStringToDate(match) {
        /** @type {?} */
        const date = new Date(0);
        /** @type {?} */
        let tzHour = 0;
        /** @type {?} */
        let tzMin = 0;
        /** @type {?} */
        const dateSetter = match[8] ? date.setUTCFullYear : date.setFullYear;
        /** @type {?} */
        const timeSetter = match[8] ? date.setUTCHours : date.setHours;
        if (match[9]) {
            tzHour = +(match[9] + match[10]);
            tzMin = +(match[9] + match[11]);
        }
        dateSetter.call(date, +(match[1]), +(match[2]) - 1, +(match[3]));
        /** @type {?} */
        const h = +(match[4] || '0') - tzHour;
        /** @type {?} */
        const m = +(match[5] || '0') - tzMin;
        /** @type {?} */
        const s = +(match[6] || '0');
        /** @type {?} */
        const ms = Math.round(parseFloat('0.' + (match[7] || 0)) * 1000);
        timeSetter.call(date, h, m, s, ms);
        return date;
    }
}
/**
 * @param {?} digits
 * @return {?}
 */
function formatDigitsAliases(digits) {
    /** @type {?} */
    const parts = digits.match(NUMBER_FORMAT_REGEXP);
    if (parts == null)
        return null;
    /** @type {?} */
    const digitsOptions = {};
    if (parts[1] != null) {
        digitsOptions.minimumIntegerDigits = parseInt(parts[1]);
    }
    if (parts[3] != null) {
        digitsOptions.minimumFractionDigits = parseInt(parts[3]);
    }
    if (parts[5] != null) {
        digitsOptions.maximumFractionDigits = parseInt(parts[5]);
    }
    return digitsOptions;
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Manages language, default locale, currency & timezone.
 */
class LocaleService {
    /**
     * @param {?} configuration
     * @param {?} storage
     */
    constructor(configuration, storage) {
        this.configuration = configuration;
        this.storage = storage;
        /**
         * Fired when the language changes. Returns the language code.
         */
        this.languageCodeChanged = new Subject();
        /**
         * Fired when the default locale changes. Returns the default locale.
         */
        this.defaultLocaleChanged = new Subject();
        /**
         * Fired when the currency changes. Returns the currency code.
         */
        this.currencyCodeChanged = new Subject();
        /**
         * Fired when the timezone changes. Returns the timezone.
         */
        this.timezoneChanged = new Subject();
        this.defaultLocale = new DefaultLocale$1();
    }
    /**
     * @return {?}
     */
    getConfiguration() {
        return this.configuration.locale;
    }
    /**
     * @return {?}
     */
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.initLanguage();
            yield this.initDefaultLocale();
            yield this.initCurrency();
            yield this.initTimezone();
        });
    }
    /**
     * @return {?}
     */
    getBrowserLanguage() {
        /** @type {?} */
        let browserLanguage = null;
        if (typeof navigator !== "undefined" && navigator.language) {
            browserLanguage = navigator.language;
        }
        if (browserLanguage != null) {
            browserLanguage = browserLanguage.split("-")[0];
        }
        return browserLanguage;
    }
    /**
     * @return {?}
     */
    getAvailableLanguages() {
        /** @type {?} */
        let languages = [];
        if (this.configuration.locale.languages) {
            languages = this.configuration.locale.languages.map((language) => language.code);
        }
        return languages;
    }
    /**
     * @param {?=} languageCode
     * @return {?}
     */
    getLanguageDirection(languageCode = this.defaultLocale.languageCode) {
        /** @type {?} */
        const matchedLanguage = this.matchLanguage(languageCode);
        return matchedLanguage ? matchedLanguage.dir : "";
    }
    /**
     * @return {?}
     */
    getCurrentLanguage() {
        return this.defaultLocale.languageCode;
    }
    /**
     * @return {?}
     */
    getCurrentCountry() {
        return this.defaultLocale.countryCode || "";
    }
    /**
     * @return {?}
     */
    getCurrentScript() {
        return this.defaultLocale.scriptCode || "";
    }
    /**
     * Returns the well formatted locale as {languageCode}[-scriptCode][-countryCode]
     * @return {?}
     */
    getCurrentLocale() {
        /** @type {?} */
        let locale = this.defaultLocale.languageCode;
        locale += !!this.defaultLocale.scriptCode ? "-" + this.defaultLocale.scriptCode : "";
        locale += !!this.defaultLocale.countryCode ? "-" + this.defaultLocale.countryCode : "";
        return locale;
    }
    /**
     * @return {?}
     */
    getCurrentNumberingSystem() {
        return this.defaultLocale.numberingSystem || "";
    }
    /**
     * @return {?}
     */
    getCurrentCalendar() {
        return this.defaultLocale.calendar || "";
    }
    /**
     * @return {?}
     */
    getDefaultLocale() {
        return this.defaultLocale.value;
    }
    /**
     * @return {?}
     */
    getCurrentCurrency() {
        return this.currencyCode;
    }
    /**
     * @param {?} currencyDisplay
     * @param {?=} defaultLocale
     * @param {?=} currency
     * @return {?}
     */
    getCurrencySymbol(currencyDisplay, defaultLocale, currency) {
        /** @type {?} */
        let currencySymbol = this.currencyCode;
        if (IntlAPI.hasNumberFormat()) {
            /** @type {?} */
            const localeZero = this.formatDecimal(0, '1.0-0', defaultLocale);
            /** @type {?} */
            const localeValue = this.formatCurrency(0, '1.0-0', currencyDisplay, defaultLocale, currency);
            currencySymbol = localeValue.replace(localeZero, "");
            currencySymbol = currencySymbol.trim();
        }
        return currencySymbol;
    }
    /**
     * @return {?}
     */
    getCurrentTimezone() {
        return this.timezone;
    }
    /**
     * @param {?} languageCode
     * @return {?}
     */
    setCurrentLanguage(languageCode) {
        if (this.defaultLocale.languageCode != languageCode) {
            this.rollbackLanguageCode = this.defaultLocale.languageCode;
            this.defaultLocale.build(languageCode);
            this.releaseLanguage();
        }
    }
    /**
     * @param {?} languageCode
     * @param {?=} countryCode
     * @param {?=} scriptCode
     * @param {?=} numberingSystem
     * @param {?=} calendar
     * @return {?}
     */
    setDefaultLocale(languageCode, countryCode, scriptCode, numberingSystem, calendar) {
        if (this.defaultLocale.languageCode != languageCode ||
            this.defaultLocale.countryCode != countryCode ||
            this.defaultLocale.scriptCode != scriptCode ||
            this.defaultLocale.numberingSystem != numberingSystem ||
            this.defaultLocale.calendar != calendar) {
            this.rollbackDefaultLocale = this.defaultLocale.value;
            this.defaultLocale.build(languageCode, countryCode, scriptCode, numberingSystem, calendar);
            this.releaseDefaultLocale();
        }
    }
    /**
     * @param {?} currencyCode
     * @return {?}
     */
    setCurrentCurrency(currencyCode) {
        if (this.currencyCode != currencyCode) {
            this.rollbackCurrencyCode = this.currencyCode;
            this.currencyCode = currencyCode;
            this.releaseCurrency();
        }
    }
    /**
     * @param {?} zoneName
     * @return {?}
     */
    setCurrentTimezone(zoneName) {
        if (this.timezone != zoneName) {
            this.rollbackTimezone = this.timezone;
            this.timezone = zoneName;
            this.releaseTimezone();
        }
    }
    /**
     * Formats a date according to default locale.
     * @param {?} value A Date, a number (milliseconds since UTC epoch) or an ISO string
     * @param {?=} format An alias or a DateTimeOptions object of the format. Default is 'mediumDate'
     * @param {?=} defaultLocale The default locale to use. Default is the current locale
     * @param {?=} timezone The time zone name. Default is the current timezone
     * @return {?}
     */
    formatDate(value, format, defaultLocale, timezone) {
        return IntlFormatter.formatDate(value, defaultLocale || this.defaultLocale.value, format || 'mediumDate', timezone || this.timezone);
    }
    /**
     * Formats a decimal number according to default locale.
     * @param {?} value The number to be formatted
     * @param {?=} digits An alias or a DigitsOptions object of the format
     * @param {?=} defaultLocale The default locale to use. Default is the current locale
     * @return {?}
     */
    formatDecimal(value, digits, defaultLocale) {
        return IntlFormatter.formatNumber(value, defaultLocale || this.defaultLocale.value, NumberFormatStyle.Decimal, digits);
    }
    /**
     * Formats a number as a percentage according to default locale.
     * @param {?} value The number to be formatted
     * @param {?=} digits An alias or a DigitsOptions object of the format
     * @param {?=} defaultLocale The default locale to use. Default is the current locale
     * @return {?}
     */
    formatPercent(value, digits, defaultLocale) {
        return IntlFormatter.formatNumber(value, defaultLocale || this.defaultLocale.value, NumberFormatStyle.Percent, digits);
    }
    /**
     * Formats a number as a currency according to default locale.
     * @param {?} value The number to be formatted
     * @param {?=} digits An alias or a DigitsOptions object of the format
     * @param {?=} currencyDisplay The format for the currency. Possible values are 'code', 'symbol', 'name'. Default is 'symbol'
     * @param {?=} defaultLocale The default locale to use. Default is the current locale
     * @param {?=} currency The currency to use. Default is the current currency
     * @return {?}
     */
    formatCurrency(value, digits, currencyDisplay, defaultLocale, currency) {
        return IntlFormatter.formatNumber(value, defaultLocale || this.defaultLocale.value, NumberFormatStyle.Currency, digits, currency || this.currencyCode, currencyDisplay || 'symbol');
    }
    /**
     * @param {?} codes
     * @return {?}
     */
    composeLocale(codes) {
        /** @type {?} */
        let locale = "";
        if (this.defaultLocale.languageCode) {
            for (const code of codes) {
                switch (code) {
                    case ISOCode.Script:
                        locale += "-" + this.defaultLocale.scriptCode;
                        break;
                    case ISOCode.Country:
                        locale += "-" + this.defaultLocale.countryCode;
                        break;
                    default:
                        locale += this.defaultLocale.languageCode;
                }
            }
        }
        return locale;
    }
    /**
     * Rollbacks to previous language, default locale, currency & timezone.
     * @return {?}
     */
    rollback() {
        if (this.rollbackLanguageCode && this.rollbackLanguageCode != this.defaultLocale.languageCode) {
            this.defaultLocale.value = this.rollbackLanguageCode;
            this.releaseLanguage();
        }
        if (this.rollbackDefaultLocale && this.rollbackDefaultLocale != this.defaultLocale.value) {
            this.defaultLocale.value = this.rollbackDefaultLocale;
            this.releaseDefaultLocale();
        }
        if (this.rollbackCurrencyCode && this.rollbackCurrencyCode != this.currencyCode) {
            this.currencyCode = this.rollbackCurrencyCode;
            this.releaseCurrency();
        }
        if (this.rollbackTimezone && this.rollbackTimezone != this.timezone) {
            this.timezone = this.rollbackTimezone;
            this.releaseTimezone();
        }
    }
    /**
     * @return {?}
     */
    initLanguage() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.configuration.locale.language) {
                if (!this.defaultLocale.languageCode) {
                    // Tries to get the language from the storage.
                    /** @type {?} */
                    const defaultLocale = yield this.storage.read("defaultLocale");
                    if (!!defaultLocale) {
                        this.defaultLocale.value = defaultLocale;
                    }
                    else {
                        // Tries to get the language from the browser.
                        /** @type {?} */
                        const browserLanguage = this.getBrowserLanguage();
                        /** @type {?} */
                        const matchedLanguage = this.matchLanguage(browserLanguage);
                        if (!!browserLanguage && matchedLanguage) {
                            this.defaultLocale.build(browserLanguage);
                        }
                        else {
                            // Uses the language set in the configuration.
                            this.defaultLocale.build(this.configuration.locale.language);
                        }
                        this.storage.write("defaultLocale", this.defaultLocale.value);
                    }
                    this.rollbackLanguageCode = this.defaultLocale.languageCode;
                    this.sendLanguageEvents();
                }
            }
        });
    }
    /**
     * @return {?}
     */
    initDefaultLocale() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.configuration.locale.defaultLocale) {
                if (!this.defaultLocale.value) {
                    /** @type {?} */
                    const defaultLocale = yield this.storage.read("defaultLocale");
                    if (!!defaultLocale) {
                        this.defaultLocale.value = defaultLocale;
                    }
                    else {
                        this.defaultLocale.build(this.configuration.locale.defaultLocale.languageCode, this.configuration.locale.defaultLocale.countryCode, this.configuration.locale.defaultLocale.scriptCode, this.configuration.locale.defaultLocale.numberingSystem, this.configuration.locale.defaultLocale.calendar);
                        this.storage.write("defaultLocale", this.defaultLocale.value);
                    }
                    this.rollbackDefaultLocale = this.defaultLocale.value;
                    this.sendDefaultLocaleEvents();
                }
            }
        });
    }
    /**
     * @return {?}
     */
    initCurrency() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.configuration.locale.currency) {
                if (!this.currencyCode) {
                    /** @type {?} */
                    const currencyCode = yield this.storage.read("currency");
                    if (!!currencyCode) {
                        this.currencyCode = currencyCode;
                    }
                    else {
                        this.currencyCode = this.configuration.locale.currency;
                        this.storage.write("currency", this.currencyCode);
                    }
                    this.rollbackCurrencyCode = this.currencyCode;
                    this.sendCurrencyEvents();
                }
            }
        });
    }
    /**
     * @return {?}
     */
    initTimezone() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.configuration.locale.timezone) {
                if (!this.timezone) {
                    /** @type {?} */
                    const zoneName = yield this.storage.read("timezone");
                    if (!!zoneName) {
                        this.timezone = zoneName;
                    }
                    else {
                        this.timezone = this.configuration.locale.timezone;
                        this.storage.write("timezone", this.timezone);
                    }
                    this.rollbackTimezone = this.timezone;
                    this.sendTimezoneEvents();
                }
            }
        });
    }
    /**
     * @param {?} languageCode
     * @return {?}
     */
    matchLanguage(languageCode) {
        /** @type {?} */
        let matchedLanguage;
        if (this.configuration.locale.languages && languageCode != null) {
            matchedLanguage = this.configuration.locale.languages.find((language) => language.code == languageCode);
        }
        return matchedLanguage;
    }
    /**
     * @return {?}
     */
    releaseLanguage() {
        this.storage.write("defaultLocale", this.defaultLocale.value);
        this.sendLanguageEvents();
    }
    /**
     * @return {?}
     */
    releaseDefaultLocale() {
        this.storage.write("defaultLocale", this.defaultLocale.value);
        this.sendDefaultLocaleEvents();
    }
    /**
     * @return {?}
     */
    releaseCurrency() {
        this.storage.write("currency", this.currencyCode);
        this.sendCurrencyEvents();
    }
    /**
     * @return {?}
     */
    releaseTimezone() {
        this.storage.write("timezone", this.timezone);
        this.sendTimezoneEvents();
    }
    /**
     * @return {?}
     */
    sendLanguageEvents() {
        this.languageCodeChanged.next(this.defaultLocale.languageCode);
    }
    /**
     * @return {?}
     */
    sendDefaultLocaleEvents() {
        this.defaultLocaleChanged.next(this.defaultLocale.value);
    }
    /**
     * @return {?}
     */
    sendCurrencyEvents() {
        this.currencyCodeChanged.next(this.currencyCode);
    }
    /**
     * @return {?}
     */
    sendTimezoneEvents() {
        this.timezoneChanged.next(this.timezone);
    }
}
LocaleService.decorators = [
    { type: Injectable }
];
/** @nocollapse */
LocaleService.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Inject, args: [L10N_CONFIG,] }] },
    { type: LocaleStorage }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
class LocalizedRouting {
    /**
     * @param {?} configuration
     * @param {?} locale
     */
    constructor(configuration, locale) {
        this.configuration = configuration;
        this.locale = locale;
    }
    /**
     * @return {?}
     */
    get router() {
        return InjectorRef.get(Router);
    }
    /**
     * @return {?}
     */
    get location() {
        return InjectorRef.get(Location);
    }
    /**
     * @return {?}
     */
    init() {
        if (this.configuration.localizedRouting.format) {
            // Parses the url to find the locale when the app starts.
            /** @type {?} */
            const path = this.location.path(true);
            this.parsePath(path);
            // Parses the url to find the locale when a navigation starts.
            this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe((data) => {
                this.redirectToPath(data.url);
            });
            // Replaces url when a navigation ends.
            this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((data) => {
                /** @type {?} */
                const url = (!!data.url && data.url != "/" && data.url == data.urlAfterRedirects) ?
                    data.url :
                    data.urlAfterRedirects;
                this.replacePath(this.locale.composeLocale((/** @type {?} */ (this.configuration.localizedRouting.format))), url);
            });
            // Replaces url when locale changes.
            this.locale.languageCodeChanged.subscribe(() => {
                this.replacePath(this.locale.composeLocale((/** @type {?} */ (this.configuration.localizedRouting.format))));
            });
            this.locale.defaultLocaleChanged.subscribe(() => {
                this.replacePath(this.locale.composeLocale((/** @type {?} */ (this.configuration.localizedRouting.format))));
            });
        }
    }
    /**
     * Parses path to find the locale.
     * @param {?=} path The path to be parsed
     * @return {?}
     */
    parsePath(path) {
        if (!path)
            return;
        /** @type {?} */
        const segment = this.getLocalizedSegment(path);
        if (segment != null) {
            /** @type {?} */
            const locale = (/** @type {?} */ (segment)).replace(/\//gi, "");
            /** @type {?} */
            const localeCodes = this.splitLocale(locale, (/** @type {?} */ (this.configuration.localizedRouting.format)));
            // Unrecognized segment.
            if (this.configuration.localizedRouting.schema) {
                if (!this.compareLocale(localeCodes, {
                    languageCode: localeCodes.languageCode,
                    scriptCode: this.getSchema(ISOCode.Script, localeCodes),
                    countryCode: this.getSchema(ISOCode.Country, localeCodes)
                }))
                    return;
            }
            if (this.configuration.locale.language) {
                this.locale.setCurrentLanguage(localeCodes.languageCode);
            }
            if (this.configuration.locale.defaultLocale) {
                this.locale.setDefaultLocale(localeCodes.languageCode, localeCodes.countryCode || this.getSchema(ISOCode.Country, localeCodes), localeCodes.scriptCode || this.getSchema(ISOCode.Script, localeCodes), this.getSchema(ExtraCode.NumberingSystem, localeCodes), this.getSchema(ExtraCode.Calendar, localeCodes));
            }
            if (this.configuration.locale.currency) {
                /** @type {?} */
                const currency = this.getSchema(ExtraCode.Currency, localeCodes);
                if (currency) {
                    this.locale.setCurrentCurrency(currency);
                }
            }
            if (this.configuration.locale.timezone) {
                /** @type {?} */
                const timezone = this.getSchema(ExtraCode.Timezone, localeCodes);
                if (timezone) {
                    this.locale.setCurrentTimezone(timezone);
                }
            }
        }
    }
    /**
     * Removes the locale from the path and navigates without pushing a new state into history.
     * @param {?} path Localized path
     * @return {?}
     */
    redirectToPath(path) {
        /** @type {?} */
        const segment = this.getLocalizedSegment(path);
        if (segment != null) {
            /** @type {?} */
            const url = path.replace(segment, "/");
            // navigateByUrl keeps the query params.
            this.router.navigateByUrl(url, { skipLocationChange: true });
        }
    }
    /**
     * Replaces the path with the locale without pushing a new state into history.
     * @param {?} locale The current locale
     * @param {?=} path The path to be replaced
     * @return {?}
     */
    replacePath(locale, path) {
        if (path) {
            if (!this.isDefaultRouting()) {
                this.location.replaceState(this.getLocalizedPath(locale, path));
            }
        }
        else {
            path = this.location.path(true);
            // Parses the path to find the locale.
            /** @type {?} */
            const segment = this.getLocalizedSegment(path);
            if (segment != null) {
                // Removes the locale from the path.
                path = path.replace(segment, "/");
                if (this.isDefaultRouting()) {
                    this.location.replaceState(path);
                }
            }
            if (!this.isDefaultRouting()) {
                this.location.replaceState(this.getLocalizedPath(locale, path));
            }
        }
    }
    /**
     * @param {?} path
     * @return {?}
     */
    getLocalizedSegment(path) {
        for (const lang of this.locale.getAvailableLanguages()) {
            /** @type {?} */
            const regex = new RegExp(`(^\/${lang}\/)|(^\/${lang}$)|(^\/${lang}-.*?\/)|(^\/${lang}-.*?$)`);
            /** @type {?} */
            const segments = path.match(regex);
            if (segments != null) {
                return segments[0];
            }
        }
        return null;
    }
    /**
     * @param {?} locale
     * @param {?} codes
     * @return {?}
     */
    splitLocale(locale, codes) {
        /** @type {?} */
        const values = locale.split("-");
        /** @type {?} */
        const localeCodes = { languageCode: "" };
        if (codes.length > 0) {
            for (let i = 0; i < codes.length; i++) {
                localeCodes[codes[i]] = values[i] || undefined;
            }
        }
        return localeCodes;
    }
    /**
     * @param {?} locale1
     * @param {?} locale2
     * @return {?}
     */
    compareLocale(locale1, locale2) {
        return locale1.languageCode == locale2.languageCode &&
            (!!locale1.scriptCode ?
                locale1.scriptCode == locale2.scriptCode : true) &&
            (!!locale1.countryCode ?
                locale1.countryCode == locale2.countryCode : true);
    }
    /**
     * @param {?} code
     * @param {?} locale
     * @return {?}
     */
    getSchema(code, locale) {
        if (!this.configuration.localizedRouting.schema)
            return undefined;
        /** @type {?} */
        const schema = this.configuration.localizedRouting.schema
            .find(((s) => this.compareLocale(locale, s)));
        return schema ? schema[code] : undefined;
    }
    /**
     * @return {?}
     */
    isDefaultRouting() {
        if (!this.configuration.localizedRouting.defaultRouting)
            return false;
        if (this.configuration.locale.language) {
            return this.locale.getCurrentLanguage() == this.configuration.locale.language;
        }
        if (this.configuration.locale.defaultLocale) {
            return this.compareLocale({
                languageCode: this.locale.getCurrentLanguage(),
                scriptCode: this.locale.getCurrentScript(),
                countryCode: this.locale.getCurrentCountry()
            }, this.configuration.locale.defaultLocale);
        }
        return false;
    }
    /**
     * @param {?} locale
     * @param {?} path
     * @return {?}
     */
    getLocalizedPath(locale, path) {
        return Location.stripTrailingSlash("/" + locale + path);
    }
}
LocalizedRouting.decorators = [
    { type: Injectable }
];
/** @nocollapse */
LocalizedRouting.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Inject, args: [L10N_CONFIG,] }] },
    { type: LocaleService }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Implement this class-interface to create a custom provider for translation data.
 * @abstract
 */
class TranslationProvider {
}
TranslationProvider.decorators = [
    { type: Injectable }
];
class L10nTranslationProvider {
    /**
     * @param {?} http
     * @param {?} configuration
     * @param {?} caching
     */
    constructor(http, configuration, caching) {
        this.http = http;
        this.configuration = configuration;
        this.caching = caching;
        this.headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        this.options = {};
    }
    /**
     * @param {?} language
     * @param {?} args
     * @return {?}
     */
    getTranslation(language, args) {
        this.setOptions();
        /** @type {?} */
        let url = "";
        switch (args.type) {
            case ProviderType.WebAPI:
                url += args.path + language;
                break;
            default:
                url += args.prefix + language + ".json";
        }
        return this.getRequest(url);
    }
    /**
     * @return {?}
     */
    setOptions() {
        this.options = {
            headers: this.headers,
            params: this.configuration.translation.version ? new HttpParams().set('ver', this.configuration.translation.version) : undefined
        };
    }
    /**
     * @param {?} url
     * @return {?}
     */
    getRequest(url) {
        /** @type {?} */
        let request;
        if (this.configuration.translation.timeout) {
            request = this.http.get(url, this.options).pipe(timeout(this.configuration.translation.timeout));
        }
        else {
            request = this.http.get(url, this.options);
        }
        if (this.configuration.translation.caching) {
            return this.caching.read(url, request);
        }
        return request;
    }
}
L10nTranslationProvider.decorators = [
    { type: Injectable }
];
/** @nocollapse */
L10nTranslationProvider.ctorParameters = () => [
    { type: HttpClient, decorators: [{ type: Optional }] },
    { type: undefined, decorators: [{ type: Inject, args: [L10N_CONFIG,] }] },
    { type: Caching }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Implement this class-interface to create a custom handler for translated values.
 * @abstract
 */
class TranslationHandler {
}
TranslationHandler.decorators = [
    { type: Injectable }
];
class L10nTranslationHandler {
    /**
     * @param {?} configuration
     */
    constructor(configuration) {
        this.configuration = configuration;
    }
    /**
     * @param {?} path
     * @param {?} key
     * @param {?} value
     * @param {?} args
     * @param {?} lang
     * @return {?}
     */
    parseValue(path, key, value, args, lang) {
        if (value == null) {
            return this.handleMissingValue(path);
        }
        else if (args) {
            return this.handleArgs(value, args);
        }
        return value;
    }
    /**
     * @param {?} path
     * @return {?}
     */
    handleMissingValue(path) {
        if (this.configuration.translation.missingValue != null) {
            return typeof this.configuration.translation.missingValue === "function"
                ? this.configuration.translation.missingValue(path)
                : this.configuration.translation.missingValue;
        }
        // The same path is returned.
        return path;
    }
    /**
     * @param {?} value
     * @param {?} args
     * @return {?}
     */
    handleArgs(value, args) {
        /** @type {?} */
        const TEMPLATE_REGEXP = /{{\s?([^{}\s]*)\s?}}/g;
        return value.replace(TEMPLATE_REGEXP, (substring, parsedKey) => {
            /** @type {?} */
            const replacer = (/** @type {?} */ (args[parsedKey]));
            return typeof replacer !== "undefined" ? replacer : substring;
        });
    }
}
L10nTranslationHandler.decorators = [
    { type: Injectable }
];
/** @nocollapse */
L10nTranslationHandler.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Inject, args: [L10N_CONFIG,] }] }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @param {?} item
 * @return {?}
 */
function isObject(item) {
    return (typeof item === "object" && !Array.isArray(item));
}
/**
 * @param {?} target
 * @param {?} source
 * @return {?}
 */
function mergeDeep(target, source) {
    /** @type {?} */
    const output = Object.assign({}, target);
    if (isObject(target) && isObject(source)) {
        Object.keys(source).forEach((key) => {
            if (isObject(source[key])) {
                if (!(key in target)) {
                    Object.assign(output, { [key]: source[key] });
                }
                else {
                    output[key] = mergeDeep(target[key], source[key]);
                }
            }
            else {
                Object.assign(output, { [key]: source[key] });
            }
        });
    }
    return output;
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Manages the translation data.
 */
class TranslationService {
    /**
     * @param {?} configuration
     * @param {?} locale
     * @param {?} translationProvider
     * @param {?} translationHandler
     * @param {?} injector
     */
    constructor(configuration, locale, translationProvider, translationHandler, injector) {
        this.configuration = configuration;
        this.locale = locale;
        this.translationProvider = translationProvider;
        this.translationHandler = translationHandler;
        this.injector = injector;
        /**
         * Fired when the translation data could not been loaded. Returns the error.
         */
        this.translationError = new Subject();
        this.translation = new BehaviorSubject('');
        /**
         * The translation data: {language: {key: value}}.
         */
        this.translationData = {};
        this.injector.translations.push(this);
    }
    /**
     * @return {?}
     */
    getConfiguration() {
        return this.configuration.translation;
    }
    /**
     * @return {?}
     */
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            // When the language or the default locale changes, loads translation data.
            race(this.locale.languageCodeChanged, this.locale.defaultLocaleChanged).subscribe(() => {
                this.loadTranslation()
                    .catch((error) => null);
            });
            yield this.loadTranslation()
                .catch((error) => { throw error; });
        });
    }
    /**
     * Forces the translation loading for the current language.
     * @return {?}
     */
    loadTranslation() {
        return __awaiter(this, void 0, void 0, function* () {
            /** @type {?} */
            let language;
            if (this.configuration.translation.composedLanguage) {
                language = this.locale.composeLocale(this.configuration.translation.composedLanguage);
            }
            else {
                language = this.locale.getCurrentLanguage();
            }
            if (language) {
                this.translationData = {};
                if (this.configuration.translation.translationData) {
                    this.getTranslation(language);
                }
                if (this.configuration.translation.providers) {
                    yield this.getTranslationAsync(language)
                        .toPromise()
                        .catch((error) => { throw error; });
                }
            }
        });
    }
    /**
     * Fired when the translation data has been loaded. Returns the translation language.
     * @return {?}
     */
    translationChanged() {
        return this.translation.asObservable();
    }
    /**
     * Fired when the latest 'translationChanged' is emitted. Returns the translation language.
     * Used when the reference to the service is not known, as in decorators.
     * @return {?}
     */
    latestTranslation() {
        /** @type {?} */
        const sequencesOfTranslation = [];
        for (const translation of this.injector.translations) {
            sequencesOfTranslation.push(translation.translationChanged());
        }
        return combineLatest(sequencesOfTranslation).pipe(filter((languages) => languages.length == sequencesOfTranslation.length &&
            languages.every((lang, i, arr) => lang == arr[0])), map((languages) => languages[0]));
    }
    /**
     * Translates a key or an array of keys.
     * @param {?} keys The key or an array of keys to be translated
     * @param {?=} args Optional parameters contained in the key
     * @param {?=} lang The current language of the service is used by default
     * @return {?} The translated value or an object: {key: value}
     */
    translate(keys, args, lang) {
        if (Array.isArray(keys)) {
            /** @type {?} */
            const data = {};
            for (const key of keys) {
                data[key] = this.translateKey(key, args, lang || this.translation.getValue());
            }
            return data;
        }
        return this.translateKey(keys, args, lang || this.translation.getValue());
    }
    /**
     * @param {?} keys
     * @param {?=} args
     * @param {?=} lang
     * @return {?}
     */
    translateAsync(keys, args, lang) {
        return Observable.create((observer) => {
            /** @type {?} */
            const values = this.translate(keys, args, lang);
            observer.next(values);
            observer.complete();
        });
    }
    /**
     * @param {?} key
     * @param {?} args
     * @param {?} lang
     * @return {?}
     */
    translateKey(key, args, lang) {
        // I18n plural.
        if (this.configuration.translation.i18nPlural && /^\d+\b/.exec(key)) {
            return this.translateI18nPlural(key, args, lang);
        }
        return this.getValue(key, args, lang);
    }
    /**
     * @param {?} key
     * @param {?} args
     * @param {?} lang
     * @return {?}
     */
    getValue(key, args, lang) {
        /** @type {?} */
        const path = key;
        /** @type {?} */
        let value = null;
        /** @type {?} */
        let translation = this.translationData[lang];
        if (translation) {
            // Composed key.
            if (this.configuration.translation.composedKeySeparator) {
                /** @type {?} */
                const sequences = key.split(this.configuration.translation.composedKeySeparator);
                key = (/** @type {?} */ (sequences.shift()));
                while (sequences.length > 0 && translation[key]) {
                    translation = translation[key];
                    key = (/** @type {?} */ (sequences.shift()));
                }
            }
            value = typeof translation[key] === "undefined" ?
                translation[this.configuration.translation.missingKey || ""] :
                translation[key];
        }
        return this.translationHandler.parseValue(path, key, value, args, lang);
    }
    /**
     * @param {?} key
     * @param {?} args
     * @param {?} lang
     * @return {?}
     */
    translateI18nPlural(key, args, lang) {
        /** @type {?} */
        let keyText = key.replace(/^\d+\b/, "");
        keyText = keyText.trim();
        /** @type {?} */
        const keyNumber = parseFloat(key);
        if (!isNaN(keyNumber)) {
            key = key.replace(/^\d+/, this.locale.formatDecimal(keyNumber));
        }
        return key.replace(keyText, this.getValue(keyText, args, lang));
    }
    /**
     * @param {?} language
     * @return {?}
     */
    getTranslation(language) {
        /** @type {?} */
        const translations = (/** @type {?} */ (this.configuration.translation.translationData)).filter((value) => value.languageCode == language);
        for (const translation of translations) {
            this.addData(translation.data, language);
        }
        if (!this.configuration.translation.providers) {
            this.releaseTranslation(language);
        }
    }
    /**
     * @param {?} language
     * @return {?}
     */
    getTranslationAsync(language) {
        return Observable.create((observer) => {
            /** @type {?} */
            const sequencesOfOrderedTranslationData = [];
            /** @type {?} */
            const sequencesOfTranslationData = [];
            for (const provider of (/** @type {?} */ (this.configuration.translation.providers))) {
                if (typeof provider.type !== "undefined" && provider.type == ProviderType.Fallback) {
                    /** @type {?} */
                    let fallbackLanguage = language;
                    if (provider.fallbackLanguage) {
                        fallbackLanguage = this.locale.composeLocale(provider.fallbackLanguage);
                    }
                    sequencesOfOrderedTranslationData.push(this.translationProvider.getTranslation(fallbackLanguage, provider));
                }
                else {
                    sequencesOfTranslationData.push(this.translationProvider.getTranslation(language, provider));
                }
            }
            // Merges all the sequences into a single observable sequence.
            /** @type {?} */
            const mergedSequencesOfTranslationData = merge(...sequencesOfTranslationData);
            // Adds to ordered sequences.
            sequencesOfOrderedTranslationData.push(mergedSequencesOfTranslationData);
            concat(...sequencesOfOrderedTranslationData).subscribe((data) => {
                this.addData(data, language);
            }, (error) => {
                this.handleError(error, language);
                observer.error(error);
                observer.complete();
            }, () => {
                this.releaseTranslation(language);
                observer.next(null);
                observer.complete();
            });
        });
    }
    /**
     * @param {?} data
     * @param {?} language
     * @return {?}
     */
    addData(data, language) {
        this.translationData[language] = typeof this.translationData[language] !== "undefined"
            ? mergeDeep(this.translationData[language], data)
            : data;
    }
    /**
     * @param {?} language
     * @return {?}
     */
    releaseTranslation(language) {
        this.translation.next(language);
    }
    /**
     * @param {?} error
     * @param {?} language
     * @return {?}
     */
    handleError(error, language) {
        if (this.configuration.translation.rollbackOnError) {
            this.locale.rollback();
        }
        else {
            this.releaseTranslation(language);
        }
        this.translationError.next(error);
    }
}
TranslationService.decorators = [
    { type: Injectable }
];
/** @nocollapse */
TranslationService.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Inject, args: [L10N_CONFIG,] }] },
    { type: LocaleService },
    { type: TranslationProvider },
    { type: TranslationHandler },
    { type: InjectorRef }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Initializes the services.
 * @abstract
 */
class L10nLoader {
}
L10nLoader.decorators = [
    { type: Injectable }
];
class LocaleLoader {
    /**
     * @param {?} locale
     * @param {?} translation
     */
    constructor(locale, translation) {
        this.locale = locale;
        this.translation = translation;
    }
    /**
     * @return {?}
     */
    load() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.locale.init();
            yield this.translation.init()
                .catch((error) => { throw error; });
        });
    }
}
LocaleLoader.decorators = [
    { type: Injectable }
];
/** @nocollapse */
LocaleLoader.ctorParameters = () => [
    { type: LocaleService },
    { type: TranslationService }
];
class TranslationLoader {
    /**
     * @param {?} translation
     */
    constructor(translation) {
        this.translation = translation;
    }
    /**
     * @return {?}
     */
    load() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.translation.init()
                .catch((error) => { throw error; });
        });
    }
}
TranslationLoader.decorators = [
    { type: Injectable }
];
/** @nocollapse */
TranslationLoader.ctorParameters = () => [
    { type: TranslationService }
];
class LocalizedRoutingLoader {
    /**
     * @param {?} localizedRouting
     * @param {?} locale
     * @param {?} translation
     */
    constructor(localizedRouting, locale, translation) {
        this.localizedRouting = localizedRouting;
        this.locale = locale;
        this.translation = translation;
    }
    /**
     * @return {?}
     */
    load() {
        return __awaiter(this, void 0, void 0, function* () {
            this.localizedRouting.init();
            yield this.locale.init();
            yield this.translation.init()
                .catch((error) => { throw error; });
        });
    }
}
LocalizedRoutingLoader.decorators = [
    { type: Injectable }
];
/** @nocollapse */
LocalizedRoutingLoader.ctorParameters = () => [
    { type: LocalizedRouting },
    { type: LocaleService },
    { type: TranslationService }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @param {?} target
 * @return {?}
 */
function takeUntilDestroyed(target) {
    /** @type {?} */
    const destroy = new Subject();
    /** @type {?} */
    const targetNgOnDestroy = target.ngOnDestroy;
    if (typeof targetNgOnDestroy === "undefined") {
        Logger.log(target.constructor ? target.constructor.name : 'takeUntilDestroyed', 'missingOnDestroy');
    }
    /**
     * @this {?}
     * @return {?}
     */
    function ngOnDestroy() {
        destroy.next(true);
        destroy.complete();
        if (targetNgOnDestroy) {
            targetNgOnDestroy.apply(this);
        }
    }
    target.ngOnDestroy = ngOnDestroy;
    return takeUntil(destroy);
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Provides 'lang' to translate pipe.
 */
class Translation {
    /**
     * @param {?=} changeDetectorRef
     */
    constructor(changeDetectorRef) {
        this.changeDetectorRef = changeDetectorRef;
        this.lang = '';
        /** @type {?} */
        const translation = InjectorRef.get(TranslationService);
        translation.latestTranslation().pipe(takeUntilDestroyed(this)).subscribe((language) => {
            this.lang = language;
            // OnPush Change Detection strategy.
            if (this.changeDetectorRef) {
                this.changeDetectorRef.markForCheck();
            }
        });
    }
    /**
     * @return {?}
     */
    ngOnDestroy() { }
}
Translation.decorators = [
    { type: Injectable }
];
/** @nocollapse */
Translation.ctorParameters = () => [
    { type: ChangeDetectorRef }
];
/**
 * Provides 'lang' to translate pipe,
 * 'defaultLocale', 'currency', 'timezone' to l10nDate, l10nDecimal, l10nPercent & l10nCurrency pipes.
 */
class Localization extends Translation {
    /**
     * @param {?=} changeDetectorRef
     */
    constructor(changeDetectorRef) {
        super();
        this.changeDetectorRef = changeDetectorRef;
        this.defaultLocale = '';
        this.currency = '';
        this.timezone = '';
        /** @type {?} */
        const locale = InjectorRef.get(LocaleService);
        this.defaultLocale = locale.getDefaultLocale();
        locale.defaultLocaleChanged.pipe(takeUntilDestroyed(this)).subscribe((defaultLocale) => {
            this.defaultLocale = defaultLocale;
            if (this.changeDetectorRef) {
                this.changeDetectorRef.markForCheck();
            }
        });
        this.currency = locale.getCurrentCurrency();
        locale.currencyCodeChanged.pipe(takeUntilDestroyed(this)).subscribe((currency) => {
            this.currency = currency;
            if (this.changeDetectorRef) {
                this.changeDetectorRef.markForCheck();
            }
        });
        this.timezone = locale.getCurrentTimezone();
        locale.timezoneChanged.pipe(takeUntilDestroyed(this)).subscribe((zoneName) => {
            this.timezone = zoneName;
            if (this.changeDetectorRef) {
                this.changeDetectorRef.markForCheck();
            }
        });
    }
}
Localization.decorators = [
    { type: Injectable }
];
/** @nocollapse */
Localization.ctorParameters = () => [
    { type: ChangeDetectorRef }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Provides the methods for locale validation.
 */
class LocaleValidation {
    /**
     * @param {?} locale
     */
    constructor(locale) {
        this.locale = locale;
    }
    /**
     * Converts a string to a number according to default locale.
     * If the string cannot be converted to a number, returns NaN.
     * @param {?} s The string to be parsed
     * @param {?=} digits An alias of the format. Default is '1.0-3'
     * @param {?=} defaultLocale The default locale to use. Default is the current locale
     * @return {?}
     */
    parseNumber(s, digits, defaultLocale) {
        if (s == "" || s == null)
            return null;
        this.decimalCode = this.getDecimalCode(defaultLocale);
        this.numberCodes = this.getNumberCodes(defaultLocale);
        if (!this.validateNumber(s, digits))
            return NaN;
        /** @type {?} */
        let value = "";
        /** @type {?} */
        const characters = s.split("");
        for (const char of characters) {
            /** @type {?} */
            const charCode = this.toUnicode(char);
            /** @type {?} */
            const index = this.numberCodes.indexOf(charCode);
            if (index != -1) {
                value += index;
            }
            else if (charCode == this.decimalCode.minusSign) {
                value += "-";
            }
            else if (charCode == this.decimalCode.decimalSeparator) {
                value += ".";
            }
            else if (charCode == this.decimalCode.thousandSeparator) {
                continue;
            }
            else {
                return NaN;
            }
        }
        return parseFloat(value);
    }
    /**
     * @param {?} s
     * @param {?=} digits
     * @return {?}
     */
    validateNumber(s, digits) {
        /** @type {?} */
        let options = {};
        if (digits) {
            /** @type {?} */
            const digitsOptions = formatDigitsAliases(digits);
            if (digitsOptions != null) {
                options = digitsOptions;
            }
            else {
                Logger.log('LocaleValidation', 'invalidNumberFormatAlias');
            }
        }
        /** @type {?} */
        const minInt = options.minimumIntegerDigits !== undefined ? options.minimumIntegerDigits : 1;
        /** @type {?} */
        const minFraction = options.minimumFractionDigits !== undefined ? options.minimumFractionDigits : 0;
        /** @type {?} */
        const maxFraction = options.maximumFractionDigits !== undefined ? options.maximumFractionDigits : 3;
        /** @type {?} */
        const minusSign = this.decimalCode.minusSign;
        /** @type {?} */
        const zero = this.numberCodes[0];
        /** @type {?} */
        const decimalSeparator = this.decimalCode.decimalSeparator;
        /** @type {?} */
        const thousandSeparator = this.decimalCode.thousandSeparator;
        /** @type {?} */
        const nine = this.numberCodes[9];
        // Pattern for 1.0-2 digits: /^-?[0-9]{1,}(\.[0-9]{0,2})?$/
        // Unicode pattern = "^\u002d?[\u0030-\u0039]{1,}(\\u002e[\u0030-\u0039]{0,2})?$"
        // Complete Pattern with thousand separator:
        // /^-?([0-9]{1,}|(?=(?:\,*[0-9]){1,}(\.|$))(?!0(?!\.|[0-9]))[0-9]{1,3}(\,[0-9]{3})*)(\.[0-9]{0,2})?$/
        // where:
        // (?=(?:\,*[0-9]){1,}(\.|$)) => Positive Lookahead to count the integer digits
        // (?!0(?!\.|[0-9])) => Negative Lookahead to avoid 0,1111.00
        // [0-9]{1,3}(\,[0-9]{3})* => Allows thousand separator
        /** @type {?} */
        const d = `[${zero}-${nine}]`;
        /** @type {?} */
        const n = `{${minInt},}`;
        /** @type {?} */
        const nm = `{${minFraction},${maxFraction}}`;
        /** @type {?} */
        const plainPattern = `${d}${n}`;
        // tslint:disable-next-line
        /** @type {?} */
        const thousandPattern = `(?=(?:\\${thousandSeparator}*${d})${n}(\\${decimalSeparator}|$))(?!${zero}(?!\\${decimalSeparator}|${d}))${d}{1,3}(\\${thousandSeparator}${d}{3})*`;
        /** @type {?} */
        let pattern = `^${minusSign}?(${plainPattern}|${thousandPattern})`;
        if (minFraction > 0 && maxFraction > 0) {
            // Decimal separator is mandatory.
            pattern += `\\${decimalSeparator}${d}${nm}$`;
        }
        else if (minFraction == 0 && maxFraction > 0) {
            // Decimal separator is optional.
            pattern += `(\\${decimalSeparator}${d}${nm})?$`;
        }
        else {
            // Integer number.
            pattern += `$`;
        }
        pattern = this.toChar(pattern);
        /** @type {?} */
        const NUMBER_REGEXP = new RegExp(pattern);
        return NUMBER_REGEXP.test(s);
    }
    /**
     * @param {?=} defaultLocale
     * @return {?}
     */
    getDecimalCode(defaultLocale) {
        /** @type {?} */
        let decimalCode = {
            minusSign: this.toUnicode("-"),
            decimalSeparator: this.toUnicode("."),
            thousandSeparator: this.toUnicode(",")
        };
        if (IntlAPI.hasNumberFormat()) {
            /** @type {?} */
            const value = -1000.9;
            // Reference value.
            /** @type {?} */
            const localeValue = this.locale.formatDecimal(value, '1.1-1', defaultLocale);
            /** @type {?} */
            const unicodeChars = [];
            for (let i = 0; i <= 7; i++) {
                unicodeChars.push(this.toUnicode(localeValue.charAt(i)));
            }
            // Right to left:
            // checks Unicode characters 'RIGHT-TO-LEFT MARK' (U+200F) & 'Arabic Letter Mark' (U+061C),
            // or the reverse order.
            // Left to right:
            // checks Unicode character 'LEFT-TO-RIGHT MARK' (U+200E).
            if (unicodeChars[0] == "\\u200F" || unicodeChars[0] == "\\u061C") {
                decimalCode = {
                    minusSign: unicodeChars[1],
                    decimalSeparator: unicodeChars[7],
                    thousandSeparator: unicodeChars[3]
                };
            }
            else if (unicodeChars[0] == this.toUnicode(this.locale.formatDecimal(1, '1.0-0', defaultLocale))) {
                decimalCode = {
                    minusSign: unicodeChars[7],
                    decimalSeparator: unicodeChars[5],
                    thousandSeparator: unicodeChars[1]
                };
            }
            else if (unicodeChars[0] == "\\u200E") {
                decimalCode = {
                    minusSign: unicodeChars[1],
                    decimalSeparator: unicodeChars[7],
                    thousandSeparator: unicodeChars[3]
                };
            }
            else {
                decimalCode = {
                    minusSign: unicodeChars[0],
                    decimalSeparator: unicodeChars[6],
                    thousandSeparator: unicodeChars[2]
                };
            }
        }
        return decimalCode;
    }
    /**
     * @param {?=} defaultLocale
     * @return {?}
     */
    getNumberCodes(defaultLocale) {
        /** @type {?} */
        const numberCodes = [];
        for (let num = 0; num <= 9; num++) {
            numberCodes.push(this.toUnicode(num.toString()));
        }
        if (IntlAPI.hasNumberFormat()) {
            for (let num = 0; num <= 9; num++) {
                numberCodes[num] = this.toUnicode(this.locale.formatDecimal(num, '1.0-0', defaultLocale));
            }
        }
        return numberCodes;
    }
    /**
     * @param {?} pattern
     * @return {?}
     */
    toChar(pattern) {
        return pattern.replace(/\\u[\dA-F]{4}/gi, (match) => {
            return String.fromCharCode(parseInt(match.replace(/\\u/g, ""), 16));
        });
    }
    /**
     * @param {?} c
     * @return {?}
     */
    toUnicode(c) {
        return "\\u" + this.toHex(c.charCodeAt(0));
    }
    /**
     * @param {?} value
     * @return {?}
     */
    toHex(value) {
        /** @type {?} */
        let hex = value.toString(16).toUpperCase();
        // With padding.
        hex = "0000".substr(0, 4 - hex.length) + hex;
        return hex;
    }
}
LocaleValidation.decorators = [
    { type: Injectable }
];
/** @nocollapse */
LocaleValidation.ctorParameters = () => [
    { type: LocaleService }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Intl.Collator APIs.
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Collator
 */
class Collator {
    /**
     * @param {?} locale
     * @param {?} translation
     */
    constructor(locale, translation) {
        this.locale = locale;
        this.translation = translation;
    }
    /**
     * Compares two keys by the value of translation according to the current language.
     * @param {?} key1
     * @param {?} key2
     * @param {?=} extension Unicode extension key, e.g. 'co-phonebk'
     * @param {?=} options Default is { usage: 'sort', sensitivity: 'variant' }
     * @return {?} A negative value if the value of translation of key1 comes before the value of translation of key2;
     *         a positive value if key1 comes after key2;
     *         0 if they are considered equal or Intl.Collator is not supported
     */
    compare(key1, key2, extension, options = { usage: 'sort', sensitivity: 'variant' }) {
        if (!IntlAPI.hasCollator())
            return 0;
        /** @type {?} */
        const value1 = this.translation.translate(key1);
        /** @type {?} */
        const value2 = this.translation.translate(key2);
        /** @type {?} */
        const locale = this.addExtension(this.locale.getCurrentLocale(), extension);
        return new Intl.Collator(locale, options).compare(value1, value2);
    }
    /**
     * Sorts an array of objects or an array of arrays according to the current language.
     * @param {?} list The array to be sorted
     * @param {?} keyName The column that contains the keys of the values to be ordered
     * @param {?=} order 'asc' or 'desc'. The default value is 'asc'
     * @param {?=} extension Unicode extension key, e.g. 'co-phonebk'
     * @param {?=} options Default is { usage: 'sort', sensitivity: 'variant' }
     * @return {?} The same sorted list or the same list if Intl.Collator is not supported
     */
    sort(list, keyName, order = "asc", extension, options = { usage: 'sort', sensitivity: 'variant' }) {
        if (!list || !keyName || !IntlAPI.hasCollator())
            return list;
        list.sort((key1, key2) => {
            return this.compare(key1[keyName], key2[keyName], extension, options);
        });
        if (order == "desc") {
            list.reverse();
        }
        return list;
    }
    /**
     * Sorts asynchronously an array of objects or an array of arrays according to the current language.
     * @param {?} list The array to be sorted
     * @param {?} keyName The column that contains the keys of the values to be ordered
     * @param {?=} order 'asc' or 'desc'. The default value is 'asc'
     * @param {?=} extension Unicode extension key, e.g. 'co-phonebk'
     * @param {?=} options Default is { usage: 'sort', sensitivity: 'variant' }
     * @return {?} An observable of the sorted list or of the same list if Intl.Collator is not supported
     */
    sortAsync(list, keyName, order, extension, options = { usage: 'sort', sensitivity: 'variant' }) {
        return Observable.create((observer) => {
            observer.next(this.sort(list, keyName, order, extension, options));
            observer.complete();
        });
    }
    /**
     * Matches a string into an array of objects or an array of arrays according to the current language.
     * @param {?} s The string to search
     * @param {?} list The array in which to search
     * @param {?} keyNames An array that contains the columns to look for
     * @param {?=} options Default is { usage: 'search' }
     * @return {?} A filtered list or the same list if Intl.Collator is not supported
     */
    search(s, list, keyNames, options = { usage: 'search' }) {
        if (!list || !keyNames || s == "" || s == null || !IntlAPI.hasCollator())
            return list;
        /** @type {?} */
        const locale = this.locale.getCurrentLocale();
        /** @type {?} */
        const collator = new Intl.Collator(locale, options);
        /** @type {?} */
        const matches = list.filter((key) => {
            /** @type {?} */
            let found = false;
            for (let i = 0; i < keyNames.length; i++) {
                if (this.match(key[keyNames[i]], s, collator)) {
                    found = true;
                    break;
                }
            }
            return found;
        });
        return matches;
    }
    /**
     * Matches asynchronously a string into an array of objects or an array of arrays according to the current language.
     * @param {?} s The string to search
     * @param {?} list The array in which to search
     * @param {?} keyNames An array that contains the columns to look for
     * @param {?=} options Default is { usage: 'search' }
     * @return {?} An observable of the filtered list or the same list if Intl.Collator is not supported
     */
    searchAsync(s, list, keyNames, options = { usage: 'search' }) {
        return Observable.create((observer) => {
            observer.next(this.search(s, list, keyNames, options));
            observer.complete();
        });
    }
    /**
     * @param {?} locale
     * @param {?=} extension
     * @return {?}
     */
    addExtension(locale, extension) {
        if (!!extension) {
            locale = locale + "-u-" + extension;
        }
        return locale;
    }
    /**
     * @param {?} key
     * @param {?} s
     * @param {?} collator
     * @return {?}
     */
    match(key, s, collator) {
        /** @type {?} */
        const value = this.translation.translate(key);
        /** @type {?} */
        const valueLength = value.length;
        /** @type {?} */
        const sLength = s.length;
        if (sLength > valueLength) {
            return false;
        }
        if (sLength == valueLength) {
            return collator.compare(value, s) == 0;
        }
        /** @type {?} */
        let found = false;
        for (let i = 0; i < valueLength - (sLength - 1); i++) {
            /** @type {?} */
            const str = value.substr(i, sLength);
            if (collator.compare(str, s) == 0) {
                found = true;
                break;
            }
        }
        return found;
    }
}
Collator.decorators = [
    { type: Injectable }
];
/** @nocollapse */
Collator.ctorParameters = () => [
    { type: LocaleService },
    { type: TranslationService }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Manages the translation of page 'title' and meta tags.
 */
class SearchService {
    /**
     * @param {?} configuration
     * @param {?} translation
     * @param {?} title
     * @param {?} meta
     */
    constructor(configuration, translation, title, meta) {
        this.configuration = configuration;
        this.translation = translation;
        this.title = title;
        this.meta = meta;
    }
    /**
     * Translates the 'title' of the page and the provided meta tags.
     * @param {?} page The key path of the page
     * @return {?}
     */
    updateHead(page) {
        this.translation.translationChanged().subscribe(() => {
            this.setTitle(page);
            if (this.configuration.search.metaTags) {
                for (const name of this.configuration.search.metaTags) {
                    this.updateTag(name, page);
                }
            }
        });
    }
    /**
     * @param {?} page
     * @return {?}
     */
    setTitle(page) {
        this.title.setTitle(this.translation.translate(page + (this.configuration.translation.composedKeySeparator || '') + 'title'));
    }
    /**
     * @param {?} name
     * @param {?} page
     * @return {?}
     */
    updateTag(name, page) {
        this.meta.updateTag({
            name: name,
            content: this.translation.translate(page + (this.configuration.translation.composedKeySeparator || '') + name)
        });
    }
}
SearchService.decorators = [
    { type: Injectable }
];
/** @nocollapse */
SearchService.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Inject, args: [L10N_CONFIG,] }] },
    { type: TranslationService },
    { type: Title },
    { type: Meta }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
class LocaleInterceptor {
    /**
     * @param {?} configuration
     * @param {?} locale
     */
    constructor(configuration, locale) {
        this.configuration = configuration;
        this.locale = locale;
    }
    /**
     * @param {?} request
     * @param {?} next
     * @return {?}
     */
    intercept(request, next) {
        if (this.configuration.localeInterceptor.format) {
            /** @type {?} */
            const locale = this.locale.composeLocale(this.configuration.localeInterceptor.format);
            if (!!locale) {
                request = request.clone({ setHeaders: { 'Accept-Language': locale } });
            }
        }
        return next.handle(request);
    }
}
LocaleInterceptor.decorators = [
    { type: Injectable }
];
/** @nocollapse */
LocaleInterceptor.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Inject, args: [L10N_CONFIG,] }] },
    { type: LocaleService }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Property decorator for components to provide the parameter to translate pipe.
 * @return {?}
 */
function Language$1() {
    /**
     * @param {?} target
     * @param {?=} propertyKey
     * @return {?}
     */
    function DecoratorFactory(target, propertyKey) {
        /** @type {?} */
        const targetNgOnInit = target.ngOnInit;
        if (typeof targetNgOnInit === "undefined") {
            Logger.log(target.constructor ? target.constructor.name : 'Language decorator', 'missingOnInit');
        }
        /**
         * @this {?}
         * @return {?}
         */
        function ngOnInit() {
            /** @type {?} */
            const translation = InjectorRef.get(TranslationService);
            if (typeof propertyKey !== "undefined") {
                translation.latestTranslation().pipe(takeUntilDestroyed(this)).subscribe((language) => {
                    this[propertyKey] = language;
                    // OnPush Change Detection strategy.
                    /** @type {?} */
                    const cdr = Object.keys(this)
                        .find((key) => this[key] && this[key]['markForCheck'] !== undefined);
                    if (cdr) {
                        this[cdr].markForCheck();
                    }
                });
            }
            if (targetNgOnInit) {
                targetNgOnInit.apply(this);
            }
        }
        target.ngOnInit = ngOnInit;
        if (typeof propertyKey !== "undefined") {
            Object.defineProperty(target, propertyKey, {
                writable: true,
                value: ''
            });
        }
    }
    return DecoratorFactory;
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Property decorator for components to provide the parameter
 * to l10nDate, l10nDecimal, l10nPercent & l10nCurrency pipes.
 * @return {?}
 */
function DefaultLocale$2() {
    /**
     * @param {?} target
     * @param {?=} propertyKey
     * @return {?}
     */
    function DecoratorFactory(target, propertyKey) {
        /** @type {?} */
        const targetNgOnInit = target.ngOnInit;
        if (typeof targetNgOnInit === "undefined") {
            Logger.log(target.constructor ? target.constructor.name : 'DefaultLocale decorator', 'missingOnInit');
        }
        /**
         * @this {?}
         * @return {?}
         */
        function ngOnInit() {
            /** @type {?} */
            const locale = InjectorRef.get(LocaleService);
            if (typeof propertyKey !== "undefined") {
                this[propertyKey] = locale.getDefaultLocale();
                locale.defaultLocaleChanged.pipe(takeUntilDestroyed(this)).subscribe((defaultLocale) => {
                    this[propertyKey] = defaultLocale;
                    /** @type {?} */
                    const cdr = Object.keys(this)
                        .find((key) => this[key] && this[key]['markForCheck'] !== undefined);
                    if (cdr) {
                        this[cdr].markForCheck();
                    }
                });
            }
            if (targetNgOnInit) {
                targetNgOnInit.apply(this);
            }
        }
        target.ngOnInit = ngOnInit;
        if (typeof propertyKey !== "undefined") {
            Object.defineProperty(target, propertyKey, {
                writable: true,
                value: ''
            });
        }
    }
    return DecoratorFactory;
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Property decorator for components to provide the parameter to the l10nCurrency pipe.
 * @return {?}
 */
function Currency() {
    /**
     * @param {?} target
     * @param {?=} propertyKey
     * @return {?}
     */
    function DecoratorFactory(target, propertyKey) {
        /** @type {?} */
        const targetNgOnInit = target.ngOnInit;
        if (typeof targetNgOnInit === "undefined") {
            Logger.log(target.constructor ? target.constructor.name : 'Currency decorator', 'missingOnInit');
        }
        /**
         * @this {?}
         * @return {?}
         */
        function ngOnInit() {
            /** @type {?} */
            const locale = InjectorRef.get(LocaleService);
            if (typeof propertyKey !== "undefined") {
                this[propertyKey] = locale.getCurrentCurrency();
                locale.currencyCodeChanged.pipe(takeUntilDestroyed(this)).subscribe((currency) => {
                    this[propertyKey] = currency;
                    /** @type {?} */
                    const cdr = Object.keys(this)
                        .find((key) => this[key] && this[key]['markForCheck'] !== undefined);
                    if (cdr) {
                        this[cdr].markForCheck();
                    }
                });
            }
            if (targetNgOnInit) {
                targetNgOnInit.apply(this);
            }
        }
        target.ngOnInit = ngOnInit;
        if (typeof propertyKey !== "undefined") {
            Object.defineProperty(target, propertyKey, {
                writable: true,
                value: ''
            });
        }
    }
    return DecoratorFactory;
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Property decorator for components to provide the parameter to the l10nDate pipe.
 * @return {?}
 */
function Timezone() {
    /**
     * @param {?} target
     * @param {?=} propertyKey
     * @return {?}
     */
    function DecoratorFactory(target, propertyKey) {
        /** @type {?} */
        const targetNgOnInit = target.ngOnInit;
        if (typeof targetNgOnInit === "undefined") {
            Logger.log(target.constructor ? target.constructor.name : 'Timezone decorator', 'missingOnInit');
        }
        /**
         * @this {?}
         * @return {?}
         */
        function ngOnInit() {
            /** @type {?} */
            const locale = InjectorRef.get(LocaleService);
            if (typeof propertyKey !== "undefined") {
                this[propertyKey] = locale.getCurrentTimezone();
                locale.timezoneChanged.pipe(takeUntilDestroyed(this)).subscribe((zoneName) => {
                    this[propertyKey] = zoneName;
                    /** @type {?} */
                    const cdr = Object.keys(this)
                        .find((key) => this[key] && this[key]['markForCheck'] !== undefined);
                    if (cdr) {
                        this[cdr].markForCheck();
                    }
                });
            }
            if (targetNgOnInit) {
                targetNgOnInit.apply(this);
            }
        }
        target.ngOnInit = ngOnInit;
        if (typeof propertyKey !== "undefined") {
            Object.defineProperty(target, propertyKey, {
                writable: true,
                value: ''
            });
        }
    }
    return DecoratorFactory;
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
class TranslatePipe {
    /**
     * @param {?} translation
     */
    constructor(translation) {
        this.translation = translation;
    }
    /**
     * @param {?} key
     * @param {?} lang
     * @param {?=} args
     * @return {?}
     */
    transform(key, lang, args) {
        if (key == null || key === "")
            return null;
        if (typeof lang === "undefined")
            Logger.log('TranslatePipe', 'missingLang');
        return this.translation.translate(key, args, lang);
    }
}
TranslatePipe.decorators = [
    { type: Pipe, args: [{
                name: 'translate',
                pure: true
            },] }
];
/** @nocollapse */
TranslatePipe.ctorParameters = () => [
    { type: TranslationService }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
class L10nDatePipe {
    /**
     * @param {?} locale
     */
    constructor(locale) {
        this.locale = locale;
    }
    /**
     * @param {?} value
     * @param {?} defaultLocale
     * @param {?=} format
     * @param {?=} timezone
     * @return {?}
     */
    transform(value, defaultLocale, format, timezone) {
        if (value == null || value === "" || value !== value)
            return null;
        if (typeof defaultLocale === "undefined")
            Logger.log('L10nDatePipe', 'missingDefaultLocale');
        return this.locale.formatDate(value, format, defaultLocale, timezone);
    }
}
L10nDatePipe.decorators = [
    { type: Pipe, args: [{
                name: 'l10nDate',
                pure: true
            },] }
];
/** @nocollapse */
L10nDatePipe.ctorParameters = () => [
    { type: LocaleService }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
class L10nDecimalPipe {
    /**
     * @param {?} locale
     */
    constructor(locale) {
        this.locale = locale;
    }
    /**
     * @param {?} value
     * @param {?} defaultLocale
     * @param {?=} digits
     * @return {?}
     */
    transform(value, defaultLocale, digits) {
        if (isEmpty(value))
            return null;
        if (typeof defaultLocale === "undefined")
            Logger.log('L10nDecimalPipe', 'missingDefaultLocale');
        return this.locale.formatDecimal(value, digits, defaultLocale);
    }
}
L10nDecimalPipe.decorators = [
    { type: Pipe, args: [{
                name: 'l10nDecimal',
                pure: true
            },] }
];
/** @nocollapse */
L10nDecimalPipe.ctorParameters = () => [
    { type: LocaleService }
];
class L10nPercentPipe {
    /**
     * @param {?} locale
     */
    constructor(locale) {
        this.locale = locale;
    }
    /**
     * @param {?} value
     * @param {?} defaultLocale
     * @param {?=} digits
     * @return {?}
     */
    transform(value, defaultLocale, digits) {
        if (isEmpty(value))
            return null;
        if (typeof defaultLocale === "undefined")
            Logger.log('L10nPercentPipe', 'missingDefaultLocale');
        return this.locale.formatPercent(value, digits, defaultLocale);
    }
}
L10nPercentPipe.decorators = [
    { type: Pipe, args: [{
                name: 'l10nPercent',
                pure: true
            },] }
];
/** @nocollapse */
L10nPercentPipe.ctorParameters = () => [
    { type: LocaleService }
];
class L10nCurrencyPipe {
    /**
     * @param {?} locale
     */
    constructor(locale) {
        this.locale = locale;
    }
    /**
     * @param {?} value
     * @param {?} defaultLocale
     * @param {?} currency
     * @param {?=} currencyDisplay
     * @param {?=} digits
     * @return {?}
     */
    transform(value, defaultLocale, currency, currencyDisplay, digits) {
        if (isEmpty(value))
            return null;
        if (typeof defaultLocale === "undefined")
            Logger.log('L10nCurrencyPipe', 'missingDefaultLocale');
        if (typeof currency === "undefined")
            Logger.log('L10nCurrencyPipe', 'missingCurrency');
        return this.locale.formatCurrency(value, digits, currencyDisplay, defaultLocale, currency);
    }
}
L10nCurrencyPipe.decorators = [
    { type: Pipe, args: [{
                name: 'l10nCurrency',
                pure: true
            },] }
];
/** @nocollapse */
L10nCurrencyPipe.ctorParameters = () => [
    { type: LocaleService }
];
/**
 * @param {?} value
 * @return {?}
 */
function isEmpty(value) {
    return value == null || value === "" || value !== value; // Checks for NaN.
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Breadth First Search (BFS) algorithm for traversing & searching tree data structure of DOM
 * explores the neighbor nodes first, before moving to the next level neighbors.
 * Time complexity: between O(1) and O(|V|^2).
 */
class BFS {
    /**
     * Target node is a non empty text node.
     * @param {?} rootNode
     * @return {?}
     */
    static getTargetNode(rootNode) {
        return BFS.walk(rootNode);
    }
    /**
     * @param {?} rootNode
     * @return {?}
     */
    static walk(rootNode) {
        /** @type {?} */
        const queue = [];
        /** @type {?} */
        let iNode;
        /** @type {?} */
        let depth = 0;
        /** @type {?} */
        let nodeToDepthIncrease = 1;
        queue.push(rootNode);
        while (queue.length > 0 && depth <= BFS.MAX_DEPTH) {
            iNode = queue.shift();
            if (BFS.isTargetNode(iNode)) {
                return iNode;
            }
            if (depth < BFS.MAX_DEPTH && iNode.childNodes != null) {
                for (const child of iNode.childNodes) {
                    if (BFS.isValidNode(child)) {
                        queue.push(child);
                    }
                }
            }
            if (--nodeToDepthIncrease == 0) {
                depth++;
                nodeToDepthIncrease = queue.length;
            }
        }
        return rootNode;
    }
    /**
     * @param {?} node
     * @return {?}
     */
    static isTargetNode(node) {
        return typeof node !== "undefined" &&
            node.nodeType == 3 &&
            node.nodeValue != null &&
            node.nodeValue.trim() != "";
    }
    /**
     * A valid node is not marked for translation.
     * @param {?} node
     * @return {?}
     */
    static isValidNode(node) {
        if (typeof node !== "undefined" && node.nodeType == 1 && node.attributes) {
            for (const attr of node.attributes) {
                if (attr && BFS.SELECTOR.test(attr.name))
                    return false;
            }
        }
        return true;
    }
}
BFS.SELECTOR = new RegExp("^l10n|translate|locale");
BFS.MAX_DEPTH = 10;

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @abstract
 */
class BaseDirective {
    /**
     * @param {?} el
     * @param {?} renderer
     */
    constructor(el, renderer) {
        this.el = el;
        this.renderer = renderer;
        this.attributes = [];
        this.destroy = new Subject();
        this.TEXT_MUTATION_CONFIG = { subtree: true, characterData: true };
        this.SELECTOR = /^l10n-/;
    }
    /**
     * @return {?}
     */
    ngAfterViewInit() {
        if (this.el && this.el.nativeElement) {
            this.element = this.el.nativeElement;
            this.renderNode = BFS.getTargetNode(this.element);
            this.getKey();
            this.getAttributes();
            this.addTextListener();
            this.setup();
        }
    }
    /**
     * @param {?} changes
     * @return {?}
     */
    ngOnChanges(changes) {
        if (!!this.key) {
            if (this.nodeValue == null || this.nodeValue == "") {
                if (!!this.valueAttribute) {
                    this.key = this.valueAttribute;
                }
                else if (!!this.innerHTMLProperty) {
                    this.key = this.innerHTMLProperty;
                }
            }
            this.replaceText();
        }
        if (this.attributes.length > 0) {
            this.replaceAttributes();
        }
    }
    /**
     * @return {?}
     */
    ngOnDestroy() {
        this.destroy.next(true);
        this.removeTextListener();
    }
    /**
     * @return {?}
     */
    getAttributesData() {
        /** @type {?} */
        const keys = this.getAttributesKeys();
        /** @type {?} */
        const data = {};
        for (const key of keys) {
            data[key] = this.getValue(key);
        }
        return data;
    }
    /**
     * @return {?}
     */
    getAttributesKeys() {
        return this.attributes.map((attr) => attr.key);
    }
    /**
     * @param {?} value
     * @return {?}
     */
    setText(value) {
        if (!!value) {
            if (!!this.nodeValue && !!this.key) {
                this.removeTextListener();
                this.renderer.setValue(this.renderNode, this.nodeValue.replace(this.key, value));
                this.addTextListener();
            }
            else if (!!this.valueAttribute) {
                this.renderer.setAttribute(this.element, "value", value);
            }
            else if (!!this.innerHTMLProperty) {
                this.renderer.setProperty(this.element, "innerHTML", value);
            }
        }
    }
    /**
     * @param {?} data
     * @return {?}
     */
    setAttributes(data) {
        for (const attr of this.attributes) {
            this.renderer.setAttribute(this.element, attr.name, data[attr.key]);
        }
    }
    /**
     * @return {?}
     */
    addTextListener() {
        if (typeof MutationObserver !== "undefined") {
            this.textObserver = new MutationObserver((mutations) => {
                this.renderNode = BFS.getTargetNode(this.element);
                this.getKey();
                this.replaceText();
            });
            this.textObserver.observe(this.renderNode, this.TEXT_MUTATION_CONFIG);
        }
    }
    /**
     * @return {?}
     */
    removeTextListener() {
        if (typeof this.textObserver !== "undefined") {
            this.textObserver.disconnect();
        }
    }
    /**
     * @return {?}
     */
    getText() {
        this.nodeValue = this.renderNode != null ? (/** @type {?} */ (this.renderNode.nodeValue)) : "";
        return !!this.nodeValue ? this.nodeValue.trim() : "";
    }
    /**
     * @return {?}
     */
    getKey() {
        if (this.element.childNodes.length > 0) {
            this.key = this.getText();
        }
        else if (!!this.valueAttribute) {
            this.key = this.valueAttribute;
        }
        else if (!!this.innerHTMLProperty) {
            this.key = this.innerHTMLProperty;
        }
    }
    /**
     * @return {?}
     */
    getAttributes() {
        if (this.element.attributes) {
            for (const attr of this.element.attributes) {
                if (attr && this.SELECTOR.test(attr.name)) {
                    /** @type {?} */
                    const name = attr.name.substr(5);
                    for (const targetAttr of this.element.attributes) {
                        if (new RegExp("^" + name + "$").test(targetAttr.name)) {
                            this.attributes.push({ name: name, key: targetAttr.value });
                        }
                    }
                }
            }
        }
    }
}
BaseDirective.propDecorators = {
    valueAttribute: [{ type: Input, args: ['value',] }],
    innerHTMLProperty: [{ type: Input, args: ['innerHTML',] }]
};

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
class TranslateDirective extends BaseDirective {
    /**
     * @param {?} translation
     * @param {?} el
     * @param {?} renderer
     */
    constructor(translation, el, renderer) {
        super(el, renderer);
        this.translation = translation;
        this.el = el;
        this.renderer = renderer;
    }
    /**
     * @param {?} params
     * @return {?}
     */
    set l10nTranslate(params) {
        this.params = params;
    }
    /**
     * @param {?} params
     * @return {?}
     */
    set translate(params) {
        this.params = params;
    }
    /**
     * @return {?}
     */
    setup() {
        this.translation.translationChanged().pipe(takeUntil(this.destroy)).subscribe(() => { this.replace(); });
    }
    /**
     * @return {?}
     */
    replace() {
        this.replaceText();
        this.replaceAttributes();
    }
    /**
     * @return {?}
     */
    replaceText() {
        if (!!this.key) {
            this.setText(this.getValue(this.key));
        }
    }
    /**
     * @return {?}
     */
    replaceAttributes() {
        if (this.attributes.length > 0) {
            /** @type {?} */
            const keys = this.getAttributesKeys();
            /** @type {?} */
            const data = this.translation.translate(keys, this.params);
            this.setAttributes(data);
        }
    }
    /**
     * @param {?} key
     * @return {?}
     */
    getValue(key) {
        return this.translation.translate(key, this.params);
    }
}
TranslateDirective.decorators = [
    { type: Directive, args: [{
                selector: '[l10nTranslate],[translate]'
            },] }
];
/** @nocollapse */
TranslateDirective.ctorParameters = () => [
    { type: TranslationService },
    { type: ElementRef },
    { type: Renderer2 }
];
TranslateDirective.propDecorators = {
    l10nTranslate: [{ type: Input }],
    translate: [{ type: Input }],
    params: [{ type: Input }]
};

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
class L10nDateDirective extends BaseDirective {
    /**
     * @param {?} locale
     * @param {?} el
     * @param {?} renderer
     */
    constructor(locale, el, renderer) {
        super(el, renderer);
        this.locale = locale;
        this.el = el;
        this.renderer = renderer;
    }
    /**
     * @param {?} format
     * @return {?}
     */
    set l10nDate(format) {
        this.format = format;
    }
    /**
     * @return {?}
     */
    setup() {
        this.replace();
        this.locale.defaultLocaleChanged.pipe(takeUntil(this.destroy)).subscribe(() => { this.replace(); });
        this.locale.timezoneChanged.pipe(takeUntil(this.destroy)).subscribe(() => { this.replace(); });
    }
    /**
     * @return {?}
     */
    replace() {
        this.replaceText();
        this.replaceAttributes();
    }
    /**
     * @return {?}
     */
    replaceText() {
        if (!!this.key) {
            this.setText(this.getValue(this.key));
        }
    }
    /**
     * @return {?}
     */
    replaceAttributes() {
        if (this.attributes.length > 0) {
            this.setAttributes(this.getAttributesData());
        }
    }
    /**
     * @param {?} key
     * @return {?}
     */
    getValue(key) {
        return this.locale.formatDate(key, this.format);
    }
}
L10nDateDirective.decorators = [
    { type: Directive, args: [{
                selector: '[l10nDate]'
            },] }
];
/** @nocollapse */
L10nDateDirective.ctorParameters = () => [
    { type: LocaleService },
    { type: ElementRef },
    { type: Renderer2 }
];
L10nDateDirective.propDecorators = {
    l10nDate: [{ type: Input }],
    format: [{ type: Input }]
};

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
class L10nDecimalDirective extends BaseDirective {
    /**
     * @param {?} locale
     * @param {?} el
     * @param {?} renderer
     */
    constructor(locale, el, renderer) {
        super(el, renderer);
        this.locale = locale;
        this.el = el;
        this.renderer = renderer;
    }
    /**
     * @param {?} digits
     * @return {?}
     */
    set l10nDecimal(digits) {
        this.digits = digits;
    }
    /**
     * @return {?}
     */
    setup() {
        this.replace();
        this.locale.defaultLocaleChanged.pipe(takeUntil(this.destroy)).subscribe(() => { this.replace(); });
    }
    /**
     * @return {?}
     */
    replace() {
        this.replaceText();
        this.replaceAttributes();
    }
    /**
     * @return {?}
     */
    replaceText() {
        if (!!this.key) {
            this.setText(this.getValue(this.key));
        }
    }
    /**
     * @return {?}
     */
    replaceAttributes() {
        if (this.attributes.length > 0) {
            this.setAttributes(this.getAttributesData());
        }
    }
    /**
     * @param {?} key
     * @return {?}
     */
    getValue(key) {
        return this.locale.formatDecimal(key, this.digits);
    }
}
L10nDecimalDirective.decorators = [
    { type: Directive, args: [{
                selector: '[l10nDecimal]'
            },] }
];
/** @nocollapse */
L10nDecimalDirective.ctorParameters = () => [
    { type: LocaleService },
    { type: ElementRef },
    { type: Renderer2 }
];
L10nDecimalDirective.propDecorators = {
    l10nDecimal: [{ type: Input }],
    digits: [{ type: Input }]
};
class L10nPercentDirective extends BaseDirective {
    /**
     * @param {?} locale
     * @param {?} el
     * @param {?} renderer
     */
    constructor(locale, el, renderer) {
        super(el, renderer);
        this.locale = locale;
        this.el = el;
        this.renderer = renderer;
    }
    /**
     * @param {?} digits
     * @return {?}
     */
    set l10nPercent(digits) {
        this.digits = digits;
    }
    /**
     * @return {?}
     */
    setup() {
        this.replace();
        this.locale.defaultLocaleChanged.pipe(takeUntil(this.destroy)).subscribe(() => { this.replace(); });
    }
    /**
     * @return {?}
     */
    replace() {
        this.replaceText();
        this.replaceAttributes();
    }
    /**
     * @return {?}
     */
    replaceText() {
        if (!!this.key) {
            this.setText(this.getValue(this.key));
        }
    }
    /**
     * @return {?}
     */
    replaceAttributes() {
        if (this.attributes.length > 0) {
            this.setAttributes(this.getAttributesData());
        }
    }
    /**
     * @param {?} key
     * @return {?}
     */
    getValue(key) {
        return this.locale.formatPercent(key, this.digits);
    }
}
L10nPercentDirective.decorators = [
    { type: Directive, args: [{
                selector: '[l10nPercent]'
            },] }
];
/** @nocollapse */
L10nPercentDirective.ctorParameters = () => [
    { type: LocaleService },
    { type: ElementRef },
    { type: Renderer2 }
];
L10nPercentDirective.propDecorators = {
    l10nPercent: [{ type: Input }],
    digits: [{ type: Input }]
};
class L10nCurrencyDirective extends BaseDirective {
    /**
     * @param {?} locale
     * @param {?} el
     * @param {?} renderer
     */
    constructor(locale, el, renderer) {
        super(el, renderer);
        this.locale = locale;
        this.el = el;
        this.renderer = renderer;
    }
    /**
     * @param {?} digits
     * @return {?}
     */
    set l10nCurrency(digits) {
        this.digits = digits;
    }
    /**
     * @return {?}
     */
    setup() {
        this.replace();
        this.locale.defaultLocaleChanged.pipe(takeUntil(this.destroy)).subscribe(() => { this.replace(); });
        this.locale.currencyCodeChanged.pipe(takeUntil(this.destroy)).subscribe(() => { this.replace(); });
    }
    /**
     * @return {?}
     */
    replace() {
        this.replaceText();
        this.replaceAttributes();
    }
    /**
     * @return {?}
     */
    replaceText() {
        if (!!this.key) {
            this.setText(this.getValue(this.key));
        }
    }
    /**
     * @return {?}
     */
    replaceAttributes() {
        if (this.attributes.length > 0) {
            this.setAttributes(this.getAttributesData());
        }
    }
    /**
     * @param {?} key
     * @return {?}
     */
    getValue(key) {
        return this.locale.formatCurrency(key, this.digits, this.currencyDisplay);
    }
}
L10nCurrencyDirective.decorators = [
    { type: Directive, args: [{
                selector: '[l10nCurrency]'
            },] }
];
/** @nocollapse */
L10nCurrencyDirective.ctorParameters = () => [
    { type: LocaleService },
    { type: ElementRef },
    { type: Renderer2 }
];
L10nCurrencyDirective.propDecorators = {
    l10nCurrency: [{ type: Input }],
    currencyDisplay: [{ type: Input }],
    digits: [{ type: Input }]
};

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Function that takes a control and returns either null when it’s valid, or an error object if it’s not.
 * @param {?} digits An alias of the format
 * @param {?=} MIN_VALUE The minimum value for the number
 * @param {?=} MAX_VALUE The maximum value for the number
 * @return {?} An error object: 'format', 'minValue' or 'maxValue'; null in case the value is valid
 */
function l10nValidateNumber(digits, MIN_VALUE = Number.MIN_VALUE, MAX_VALUE = Number.MAX_VALUE) {
    /** @type {?} */
    const localeValidation = InjectorRef.get(LocaleValidation);
    return (c) => {
        if (c.value == "" || c.value == null)
            return null;
        /** @type {?} */
        const parsedValue = localeValidation.parseNumber(c.value, digits);
        if (parsedValue != null && !isNaN(parsedValue)) {
            if (parsedValue < MIN_VALUE) {
                return { minValue: true };
            }
            else if (parsedValue > MAX_VALUE) {
                return { maxValue: true };
            }
            return null; // The number is valid.
        }
        else {
            return { format: true };
        }
    };
}
class L10nNumberValidatorDirective {
    constructor() {
        this.MIN_VALUE = Number.MIN_VALUE;
        this.MAX_VALUE = Number.MAX_VALUE;
    }
    /**
     * @param {?} digits
     * @return {?}
     */
    set l10nValidateNumber(digits) {
        this.digits = digits;
    }
    /**
     * @return {?}
     */
    ngOnInit() {
        this.validator = l10nValidateNumber(this.digits, this.minValue || this.MIN_VALUE, this.maxValue || this.MAX_VALUE);
    }
    /**
     * @param {?} c
     * @return {?}
     */
    validate(c) {
        return this.validator(c);
    }
}
L10nNumberValidatorDirective.decorators = [
    { type: Directive, args: [{
                selector: '[l10nValidateNumber][ngModel],[l10nValidateNumber][formControl],[l10nValidateNumber][formControlName]',
                providers: [
                    { provide: NG_VALIDATORS, useExisting: forwardRef(() => L10nNumberValidatorDirective), multi: true }
                ]
            },] }
];
L10nNumberValidatorDirective.propDecorators = {
    l10nValidateNumber: [{ type: Input }],
    digits: [{ type: Input }],
    minValue: [{ type: Input }],
    maxValue: [{ type: Input }]
};

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
class L10nJsonLdComponent {
    /**
     * @param {?} translation
     * @param {?} sanitizer
     */
    constructor(translation, sanitizer) {
        this.translation = translation;
        this.sanitizer = sanitizer;
        this.destroy = new Subject();
    }
    /**
     * @return {?}
     */
    ngOnInit() {
        this.translation.translationChanged().pipe(takeUntil(this.destroy)).subscribe(() => {
            /** @type {?} */
            const schema = this.translation.translate(this.path);
            this.jsonLD = schema ? this.sanitize(schema) : "";
        });
    }
    /**
     * @return {?}
     */
    ngOnDestroy() {
        this.destroy.next(true);
    }
    /**
     * @param {?} schema
     * @return {?}
     */
    sanitize(schema) {
        /** @type {?} */
        const json = JSON.stringify(schema, null, 2);
        /** @type {?} */
        const html = `<script type="application/ld+json">${json}</script>`;
        return this.sanitizer.bypassSecurityTrustHtml(html);
    }
}
L10nJsonLdComponent.decorators = [
    { type: Component, args: [{
                selector: 'l10n-json-ld',
                template: ''
            }] }
];
/** @nocollapse */
L10nJsonLdComponent.ctorParameters = () => [
    { type: TranslationService },
    { type: DomSanitizer }
];
L10nJsonLdComponent.propDecorators = {
    path: [{ type: Input }],
    jsonLD: [{ type: HostBinding, args: ['innerHTML',] }]
};

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @param {?} l10nConfig
 * @param {?} token
 * @return {?}
 */
function provideRoot(l10nConfig, token) {
    return [
        InjectorRef,
        Logger,
        Caching,
        { provide: L10N_CONFIG, useValue: l10nConfigFactory(l10nConfig) },
        LocaleService,
        { provide: LocaleStorage, useClass: token.localeStorage || L10nStorage },
        TranslationService,
        { provide: TranslationProvider, useClass: token.translationProvider || L10nTranslationProvider },
        { provide: TranslationHandler, useClass: token.translationHandler || L10nTranslationHandler },
        { provide: L10nLoader, useClass: LocaleLoader }
    ];
}
/**
 * @param {?} l10nConfig
 * @param {?} token
 * @return {?}
 */
function provideChild(l10nConfig, token) {
    return [
        { provide: L10N_CONFIG, useValue: l10nConfigFactory(l10nConfig) },
        TranslationService,
        { provide: TranslationProvider, useClass: token.translationProvider || L10nTranslationProvider },
        { provide: TranslationHandler, useClass: token.translationHandler || L10nTranslationHandler },
        { provide: L10nLoader, useClass: TranslationLoader }
    ];
}
/**
 * Provides dependencies, pipes & directives for translating messages.
 */
class TranslationModule {
    /**
     * @param {?} injector
     * @param {?} logger
     */
    constructor(injector, logger) {
        this.injector = injector;
        this.logger = logger;
    }
    /**
     * Use in AppModule: new instances of LocaleService & TranslationService.
     * @param {?} l10nConfig
     * @param {?=} token
     * @return {?}
     */
    static forRoot(l10nConfig, token = {}) {
        return {
            ngModule: TranslationModule,
            providers: provideRoot(l10nConfig, token)
        };
    }
    /**
     * Use in feature modules with lazy loading: new instance of TranslationService.
     * @param {?} l10nConfig
     * @param {?=} token
     * @return {?}
     */
    static forChild(l10nConfig, token = {}) {
        return {
            ngModule: TranslationModule,
            providers: provideChild(l10nConfig, token)
        };
    }
}
TranslationModule.decorators = [
    { type: NgModule, args: [{
                declarations: [
                    TranslatePipe,
                    TranslateDirective
                ],
                exports: [
                    TranslatePipe,
                    TranslateDirective
                ]
            },] }
];
/** @nocollapse */
TranslationModule.ctorParameters = () => [
    { type: InjectorRef },
    { type: Logger }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Provides dependencies, pipes & directives for translating messages, dates & numbers.
 */
class LocalizationModule {
    /**
     * @param {?} injector
     * @param {?} logger
     */
    constructor(injector, logger) {
        this.injector = injector;
        this.logger = logger;
    }
    /**
     * Use in AppModule: new instances of LocaleService & TranslationService.
     * @param {?} l10nConfig
     * @param {?=} token
     * @return {?}
     */
    static forRoot(l10nConfig, token = {}) {
        return {
            ngModule: LocalizationModule,
            providers: provideRoot(l10nConfig, token)
        };
    }
    /**
     * Use in feature modules with lazy loading: new instance of TranslationService.
     * @param {?} l10nConfig
     * @param {?=} token
     * @return {?}
     */
    static forChild(l10nConfig, token = {}) {
        return {
            ngModule: LocalizationModule,
            providers: provideChild(l10nConfig, token)
        };
    }
}
LocalizationModule.decorators = [
    { type: NgModule, args: [{
                declarations: [
                    L10nDatePipe,
                    L10nDecimalPipe,
                    L10nPercentPipe,
                    L10nCurrencyPipe,
                    L10nDateDirective,
                    L10nDecimalDirective,
                    L10nPercentDirective,
                    L10nCurrencyDirective
                ],
                imports: [
                    TranslationModule
                ],
                exports: [
                    TranslationModule,
                    L10nDatePipe,
                    L10nDecimalPipe,
                    L10nPercentPipe,
                    L10nCurrencyPipe,
                    L10nDateDirective,
                    L10nDecimalDirective,
                    L10nPercentDirective,
                    L10nCurrencyDirective
                ]
            },] }
];
/** @nocollapse */
LocalizationModule.ctorParameters = () => [
    { type: InjectorRef },
    { type: Logger }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Provides dependencies & directives for validation by locales.
 */
class LocaleValidationModule {
    /**
     * Use in AppModule: new instance of LocaleValidation.
     * @return {?}
     */
    static forRoot() {
        return {
            ngModule: LocaleValidationModule,
            providers: [LocaleValidation]
        };
    }
}
LocaleValidationModule.decorators = [
    { type: NgModule, args: [{
                declarations: [
                    L10nNumberValidatorDirective
                ],
                exports: [
                    L10nNumberValidatorDirective
                ]
            },] }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Provides dependencies for sorting and filtering a list by locales.
 */
class CollatorModule {
}
CollatorModule.decorators = [
    { type: NgModule, args: [{
                providers: [Collator]
            },] }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Provides dependencies & components for SEO by locales.
 */
class LocaleSeoModule {
    /**
     * Use in AppModule: new instance of SearchService.
     * @return {?}
     */
    static forRoot() {
        return {
            ngModule: LocaleSeoModule,
            providers: [
                LocalizedRouting,
                SearchService,
                { provide: L10nLoader, useClass: LocalizedRoutingLoader }
            ]
        };
    }
    /**
     * Use in feature modules with lazy loading: new instance of SearchService.
     * @return {?}
     */
    static forChild() {
        return {
            ngModule: LocaleSeoModule,
            providers: [
                SearchService
            ]
        };
    }
}
LocaleSeoModule.decorators = [
    { type: NgModule, args: [{
                declarations: [L10nJsonLdComponent],
                exports: [L10nJsonLdComponent]
            },] }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Sets locale in 'Accept-Language' header on outgoing requests.
 */
class LocaleInterceptorModule {
}
LocaleInterceptorModule.decorators = [
    { type: NgModule, args: [{
                providers: [
                    { provide: HTTP_INTERCEPTORS, useClass: LocaleInterceptor, multi: true }
                ]
            },] }
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

export { InjectorRef, Logger, Caching, StorageStrategy, ProviderType, ISOCode, LogLevel, L10N_CONFIG, l10nConfigFactory, LocalizedRouting, L10nLoader, LocaleLoader, TranslationLoader, LocalizedRoutingLoader, LocaleService, LocaleStorage, L10nStorage, TranslationService, TranslationProvider, L10nTranslationProvider, TranslationHandler, L10nTranslationHandler, Translation, Localization, LocaleValidation, Collator, SearchService, LocaleInterceptor, IntlAPI, Language$1 as Language, DefaultLocale$2 as DefaultLocale, Currency, Timezone, TranslatePipe, L10nDatePipe, L10nDecimalPipe, L10nPercentPipe, L10nCurrencyPipe, BaseDirective, TranslateDirective, L10nDateDirective, L10nDecimalDirective, L10nPercentDirective, L10nCurrencyDirective, L10nNumberValidatorDirective, l10nValidateNumber, L10nJsonLdComponent, TranslationModule, provideRoot, provideChild, LocalizationModule, LocaleValidationModule, CollatorModule, LocaleSeoModule, LocaleInterceptorModule };
//# sourceMappingURL=angular-l10n.js.map
